/* eslint-disable react/react-in-jsx-scope */
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardHeader } from "@/components/ui/card";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { Input } from "@/components/ui/input";
import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from "@/components/ui/popover";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { useFetchDataV2 } from "@/hooks/useFetchDataV2";
import { convertEuroToCurrency, convertToEuro } from "@/libs/amount";
import { cn } from "@/libs/utils";
import {
  ChevronDown,
  ChevronRight,
  Dot,
  ExternalLink,
  Info,
  Loader2,
  Plus,
} from "lucide-react";
import {
  Dispatch,
  SetStateAction,
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
} from "react";
import FieldInput from "../FieldInput";
import FundingRow from "./FundingRow";
import FundingSkeleton from "./FundingSkeleton";
import FundingTrackingIntroPanel, {
  CalendarDetailItem,
  CalendarEventInput,
  FundingReminderItem,
  IntroStat,
} from "./FundingTrackingIntroPanel";
import FundingTrackingTutorial from "./FundingTrackingTutorial";
import {
  fetchHexaRateRateToEuroAtDate,
  readRealFundingContractDateRaw,
  readRealFundingDateUsesFallback,
} from "./realFundingHistoricalRate";

type PaidFile = { id: number; name?: string; uri?: string };

type RealFundingExpectedPaid = {
  id?: number;
  realfunding: number;
  amount?: number; // EUR
  at: string | Date | null; // date prévue
  status?: number; // 1=prévu, 2=reçu
  datePaid?: string | Date | null; // date virement (si reçu)
  paymentSlip?: any;
  pjs?: PaidFile[];
};

type RealFundingPaid = {
  id?: number;
  realfunding: number;
  amount?: number; // EUR
  at: string | Date | null; // date virement
  pjs?: PaidFile[];
};

type RealFunding = {
  id: number;
  paidAmount?: number; // EUR (contrat)
  paidAmountEUR?: number;
  paidAmountConverted?: number;
  paid_amount_converted?: number;
  oldRate?: number | null;
  old_rate?: number | null;
  dateAmount?: string | Date | null; // date signature
  financier?: { id: number; fullname?: string } | number;
  financer?: { id: number; fullname?: string } | number; // fallback
  paidDevise?: { id?: number; code?: string; symbol?: string } | number | null;
  paid_devise?: { id?: number; code?: string; symbol?: string } | number | null;
  representant?: any;
  project?: ProjectApi;
  pjs?: PaidFile[]; // PJ contrat si dispo
  realFundingExpectedPaids?: RealFundingExpectedPaid[];
  realFundingPaids?: RealFundingPaid[];
  verdeo?: boolean;
};

type ProjectApi = {
  id: number;
  name: string;
  country?: { currency?: any };
  desiredFunding?: any[];
  realFundings?: RealFunding[];
};

type Funder = {
  id: number;
  fullname: string;
  structureName?: string;
  representants?: any[];
  [key: string]: any;
};

type ProjectIndicator = {
  id: number;
  project?: { id: number };
  year: number;
  month: number; // parfois 0..11, parfois 1..12
  type: number; // 1/2/3
  value?: number; // EUR
  valueDisplay?: number;
  financer?: { id: number } | number;
  financier?: { id: number } | number;
  landPayment?: { realFunding?: { financier?: { id: number } | number } };
};

type SaleProgram = { id: number; name: string };
type SaleProject = { id: number; name: string; program?: SaleProgram | null };

function toNumber(v: any): number {
  const n = Number(v ?? 0);
  return !n || Number.isNaN(n) ? 0 : n;
}

function round2(n: number): number {
  return Math.round((n + Number.EPSILON) * 100) / 100;
}

function toDate(d: any): Date | null {
  if (!d) return null;
  const dt = new Date(d);
  return Number.isNaN(dt.getTime()) ? null : dt;
}

function normalizeMonth(m: any): number {
  const n = Number(m);
  if (Number.isNaN(n)) return -1;
  if (n >= 0 && n <= 11) return n;
  if (n >= 1 && n <= 12) return n - 1;
  return -1;
}

function formatEUR(n: number) {
  return (n || 0).toLocaleString("fr-FR", {
    minimumFractionDigits: 2,
    maximumFractionDigits: 2,
  });
}

function readFundingConvertedAmount(funding: any): number | null {
  const n = Number(
    funding?.paidAmountConverted ??
      funding?.paid_amount_converted ??
      funding?.paid_amountConverted ??
      funding?.paidamountconverted,
  );
  return Number.isFinite(n) && n > 0 ? n : null;
}

function readFundingRateToEuro(funding: any): number | null {
  const amountEur = Number(funding?.paidAmountEUR ?? funding?.paidAmount);
  if (!Number.isFinite(amountEur) || amountEur <= 0) return null;

  const amountConverted = readFundingConvertedAmount(funding);
  if (!amountConverted) return null;

  const rate = amountEur / amountConverted;
  return Number.isFinite(rate) && rate > 0 ? rate : null;
}

function readPaymentConvertedAmount(payment: any): number | null {
  const n = Number(
    payment?.amountConverted ??
      payment?.amount_converted ??
      payment?.paidAmountConverted ??
      payment?.paid_amount_converted,
  );
  return Number.isFinite(n) && n > 0 ? n : null;
}

function normalizeCollectionLike(value: any): any[] {
  if (Array.isArray(value)) return value;
  if (!value || typeof value !== "object") return [];

  const values = Object.values(value);
  if (!values.length) return [];

  const looksLikeEntityCollection = values.every(
    (item) => item && typeof item === "object",
  );
  return looksLikeEntityCollection ? (values as any[]) : [];
}

function readCodeFromCurrencyLike(value: any): string | null {
  if (!value) return null;
  if (typeof value === "string") {
    const code = value.trim().toUpperCase();
    return code || null;
  }
  if (typeof value === "object") {
    const code = String(value?.code ?? value?.currencyCode ?? "")
      .trim()
      .toUpperCase();
    return code || null;
  }
  return null;
}

function readIdFromCurrencyLike(value: any): number | null {
  const raw =
    typeof value === "object"
      ? value?.id
      : typeof value === "number"
        ? value
        : null;
  const id = Number(raw);
  return Number.isFinite(id) && id > 0 ? id : null;
}

function readFundingInputCurrencyCode(funding: any): string | null {
  const obtainedRows = normalizeCollectionLike(
    funding?.obtainedFundings ??
      funding?.obtainedFunding ??
      funding?.obtained_fundings ??
      funding?.obtained_funding,
  );

  for (const row of obtainedRows) {
    const code =
      readCodeFromCurrencyLike(
        row?.currency ??
          row?.devise ??
          row?.currencySelected ??
          row?.deviseSelected ??
          row?.paidDevise ??
          row?.estimatedDevise ??
          row?.deviseAmount ??
          row?.currencyAmount,
      ) ??
      (Number(
        row?.currency ??
          row?.devise ??
          row?.currencySelected ??
          row?.deviseSelected ??
          row?.paidDevise ??
          row?.estimatedDevise ??
          row?.deviseAmount ??
          row?.currencyAmount,
      ) === 3
        ? "EUR"
        : null);
    if (code) return code;
  }

  const directCode =
    readCodeFromCurrencyLike(funding?.paidDevise) ??
    readCodeFromCurrencyLike(funding?.paid_devise) ??
    readCodeFromCurrencyLike(funding?.devise);
  if (directCode) return directCode;

  const directId =
    readIdFromCurrencyLike(funding?.paidDevise) ??
    readIdFromCurrencyLike(funding?.paid_devise) ??
    readIdFromCurrencyLike(funding?.devise);
  if (directId === 3) return "EUR";

  const projectCurrency = funding?.project?.country?.currency;
  const projectCurrencyId = readIdFromCurrencyLike(projectCurrency);
  const projectCurrencyCode = readCodeFromCurrencyLike(projectCurrency);
  if (
    directId &&
    projectCurrencyId &&
    directId === projectCurrencyId &&
    projectCurrencyCode
  ) {
    return projectCurrencyCode;
  }

  return null;
}

function isFundingInputInLocalCurrency(funding: any): boolean {
  const code = readFundingInputCurrencyCode(funding);
  return !!code && code !== "EUR";
}

function readFundingCurrencyCode(funding: any): string | null {
  const candidates = [
    funding?.project?.country?.currency?.code,
    funding?.paidDevise?.code,
    funding?.paid_devise?.code,
    funding?.devise?.code,
  ];
  for (const raw of candidates) {
    if (typeof raw === "string" && raw.trim()) {
      return raw.trim().toUpperCase();
    }
  }
  return null;
}

function readFundingCurrencySymbol(funding: any): string | null {
  const candidates = [
    funding?.project?.country?.currency?.symbol,
    funding?.paidDevise?.symbol,
    funding?.paid_devise?.symbol,
    funding?.devise?.symbol,
  ];
  for (const raw of candidates) {
    if (typeof raw === "string" && raw.trim()) {
      return raw.trim();
    }
  }
  return null;
}

async function readFundingAmountEurFromContractRate(
  funding: any,
): Promise<number> {
  const amountConverted = readFundingConvertedAmount(funding);
  const storedAmountEur = toNumber(
    funding?.paidAmountEUR ?? funding?.paidAmount,
  );

  if (!isFundingInputInLocalCurrency(funding)) return storedAmountEur;
  if (!amountConverted) return storedAmountEur;

  const currencyCode = readFundingCurrencyCode(funding);
  const historicalRateToEuro =
    currencyCode && currencyCode !== "EUR"
      ? await fetchHexaRateRateToEuroAtDate(
          currencyCode,
          readRealFundingContractDateRaw(funding),
        )
      : currencyCode === "EUR"
        ? 1
        : null;
  const fallbackStoredRateToEuro = readFundingRateToEuro(funding);
  const effectiveRateToEuro =
    historicalRateToEuro &&
    Number.isFinite(historicalRateToEuro) &&
    historicalRateToEuro > 0
      ? historicalRateToEuro
      : fallbackStoredRateToEuro &&
          Number.isFinite(fallbackStoredRateToEuro) &&
          fallbackStoredRateToEuro > 0
        ? fallbackStoredRateToEuro
        : null;

  if (!effectiveRateToEuro) return storedAmountEur;
  return round2(amountConverted * effectiveRateToEuro);
}

async function normalizeFundingsContractAmountsForEurDisplay(
  fundings: RealFunding[],
): Promise<RealFunding[]> {
  return Promise.all(
    (fundings || []).map(async (funding) => {
      const amountEur = await readFundingAmountEurFromContractRate(funding);
      return {
        ...funding,
        paidAmount: amountEur,
        paidAmountEUR: amountEur,
      };
    }),
  );
}

async function normalizeProjectsContractAmountsForEurDisplay(
  projects: ProjectApi[],
): Promise<ProjectApi[]> {
  return Promise.all(
    (projects || []).map(async (project) => ({
      ...project,
      realFundings: await normalizeFundingsContractAmountsForEurDisplay(
        Array.isArray(project?.realFundings) ? project.realFundings : [],
      ),
    })),
  );
}

function readFunderIdFromFunding(rf: any): number | null {
  const f1 = rf?.financier;
  const f2 = rf?.financer;
  const id =
    typeof f1 === "number"
      ? f1
      : typeof f2 === "number"
        ? f2
        : (f1?.id ?? f2?.id);
  const n = Number(id);
  return !n || Number.isNaN(n) ? null : n;
}

function readIndicatorFunderId(ind: ProjectIndicator): number | null {
  const raw =
    (typeof ind?.financer === "number"
      ? ind.financer
      : (ind.financer as any)?.id) ??
    (typeof ind?.financier === "number"
      ? ind.financier
      : (ind.financier as any)?.id) ??
    (typeof ind?.landPayment?.realFunding?.financier === "number"
      ? ind.landPayment?.realFunding?.financier
      : (ind.landPayment?.realFunding?.financier as any)?.id);

  const n = Number(raw);
  return !n || Number.isNaN(n) ? null : n;
}

function computeFundingArrays(year: number, fundings: RealFunding[]) {
  const rec = Array(12).fill(0) as number[]; // contrats signés
  const virPrevu = Array(12).fill(0) as number[]; // versements prévus
  const virRecu = Array(12).fill(0) as number[]; // versements reçus

  for (const f of fundings || []) {
    const amountContract = toNumber(f?.paidAmount);
    const dContract = toDate(f?.dateAmount);

    const y = dContract ? dContract.getFullYear() : year;
    const m = dContract ? dContract.getMonth() : 0;
    if (amountContract && y === year) rec[m] += amountContract;

    const expected = Array.isArray(f?.realFundingExpectedPaids)
      ? f.realFundingExpectedPaids
      : [];
    for (const p of expected) {
      const amount = toNumber((p as any)?.amount);
      const d = toDate((p as any)?.at);
      if (!amount || !d || d.getFullYear() !== year) continue;
      virPrevu[d.getMonth()] += amount;
    }

    const paids = Array.isArray(f?.realFundingPaids) ? f.realFundingPaids : [];
    if (paids.length > 0) {
      for (const p of paids) {
        const amount = toNumber((p as any)?.amount);
        const d = toDate((p as any)?.at);
        if (!amount || !d || d.getFullYear() !== year) continue;
        virRecu[d.getMonth()] += amount;
      }
    } else {
      const receivedExpected = expected.filter((x: any) => x?.status == 2);
      for (const p of receivedExpected) {
        const amount = toNumber((p as any)?.amount);
        const d = toDate((p as any)?.datePaid) ?? toDate((p as any)?.at);
        if (!amount || !d || d.getFullYear() !== year) continue;
        virRecu[d.getMonth()] += amount;
      }
    }
  }

  return { recDisplay: rec, virDisplayPrevu: virPrevu, virDisplay: virRecu };
}

type FundingDisplayArrays = {
  recDisplay: number[];
  virDisplayPrevu: number[];
  virDisplay: number[];
};

type GroupDisplayArrays = FundingDisplayArrays & {
  desiredDisplay: number[];
  indPrevus: number[];
  indRealises: number[];
  indCertifies: number[];
};

type InferredCurrency = {
  code: string | null;
  symbol: string;
  currency: any | null;
};

function normalizeCurrencyCode(value: any): string | null {
  const normalized = String(value ?? "")
    .trim()
    .toUpperCase();
  return normalized || null;
}

function readCurrencyObjectWithCode(value: any): any | null {
  if (!value || typeof value !== "object") return null;
  const code = normalizeCurrencyCode((value as any)?.code);
  return code ? value : null;
}

function inferGroupLocalCurrency(group: FunderGroup): InferredCurrency {
  const projectCurrencies = (group?.projects || [])
    .map((p) => readCurrencyObjectWithCode(p?.projectRaw?.country?.currency))
    .filter((currency): currency is any => !!currency);

  const fundingCurrencies = (group?.projects || [])
    .flatMap((p) => p?.fundings || [])
    .map((funding) =>
      readCurrencyObjectWithCode(
        funding?.project?.country?.currency ??
          funding?.paidDevise ??
          funding?.paid_devise ??
          null,
      ),
    )
    .filter((currency): currency is any => !!currency);

  const combined = [...projectCurrencies, ...fundingCurrencies];
  const firstNonEur = combined.find(
    (currency) => normalizeCurrencyCode(currency?.code) !== "EUR",
  );

  const inferredCodeFromFunding = (group?.projects || [])
    .flatMap((p) => p?.fundings || [])
    .map((funding) => readFundingCurrencyCode(funding))
    .find((code) => !!code && code !== "EUR");

  const code =
    normalizeCurrencyCode(firstNonEur?.code) ??
    normalizeCurrencyCode(inferredCodeFromFunding);

  if (!code || code === "EUR") {
    return { code: null, symbol: "", currency: null };
  }

  const symbolFromObjects =
    firstNonEur?.symbol ||
    combined.find((currency) => normalizeCurrencyCode(currency?.code) === code)
      ?.symbol;
  const symbolFromFunding = (group?.projects || [])
    .flatMap((p) => p?.fundings || [])
    .map((funding) => readFundingCurrencySymbol(funding))
    .find((symbol) => !!symbol);

  return {
    code,
    symbol: symbolFromObjects || symbolFromFunding || code,
    currency: combined.find(
      (currency) => normalizeCurrencyCode(currency?.code) === code,
    ) ?? { code, symbol: symbolFromObjects || symbolFromFunding || code },
  };
}

async function convertAmountFromEurToCurrency(
  amountEur: number,
  currency: any | null,
): Promise<number> {
  const amount = toNumber(amountEur);
  if (!amount) return 0;
  const code = normalizeCurrencyCode(currency?.code);
  if (!currency || !code || code === "EUR") return amount;

  try {
    const converted = await convertEuroToCurrency(amount, currency);
    return toNumber(converted);
  } catch {
    return amount;
  }
}

async function convertMonthlyArrayFromEurToCurrency(
  values: number[],
  currency: any | null,
): Promise<number[]> {
  if (!Array.isArray(values) || values.length === 0) {
    return Array(12).fill(0) as number[];
  }
  const code = normalizeCurrencyCode(currency?.code);
  if (!currency || !code || code === "EUR") {
    return values.map((v) => toNumber(v));
  }

  return Promise.all(
    values.map((value) =>
      convertAmountFromEurToCurrency(toNumber(value), currency),
    ),
  );
}

async function computeFundingArraysForDisplayCurrency(
  year: number,
  fundings: RealFunding[],
  displayCurrencyCode: string,
  displayCurrency: any | null,
): Promise<FundingDisplayArrays> {
  const normalizedDisplayCode = normalizeCurrencyCode(displayCurrencyCode);
  if (
    !displayCurrency ||
    !normalizedDisplayCode ||
    normalizedDisplayCode === "EUR"
  ) {
    return computeFundingArrays(year, fundings);
  }

  const rec = Array(12).fill(0) as number[];
  const virPrevu = Array(12).fill(0) as number[];
  const virRecu = Array(12).fill(0) as number[];

  for (const f of fundings || []) {
    const amountContractEur = toNumber(f?.paidAmount);
    const dContract = toDate(f?.dateAmount);
    const y = dContract ? dContract.getFullYear() : year;
    const m = dContract ? dContract.getMonth() : 0;
    const fundingInputIsLocalCurrency = isFundingInputInLocalCurrency(f);
    const contractRateFromApi = await fetchHexaRateRateToEuroAtDate(
      normalizedDisplayCode,
      readRealFundingContractDateRaw(f),
    );
    const fallbackStoredRate = readFundingRateToEuro(f);
    const effectiveContractRateToEuro =
      contractRateFromApi &&
      Number.isFinite(contractRateFromApi) &&
      contractRateFromApi > 0
        ? contractRateFromApi
        : fallbackStoredRate &&
            Number.isFinite(fallbackStoredRate) &&
            fallbackStoredRate > 0
          ? fallbackStoredRate
          : null;

    if (amountContractEur && y === year) {
      const storedLocalAmount = toNumber(readFundingConvertedAmount(f));
      if (fundingInputIsLocalCurrency && storedLocalAmount > 0) {
        rec[m] += storedLocalAmount;
      } else if (effectiveContractRateToEuro) {
        rec[m] += amountContractEur / effectiveContractRateToEuro;
      } else {
        rec[m] += await convertAmountFromEurToCurrency(
          amountContractEur,
          displayCurrency,
        );
      }
    }

    const expected = Array.isArray(f?.realFundingExpectedPaids)
      ? f.realFundingExpectedPaids
      : [];
    for (const p of expected) {
      const amount = toNumber((p as any)?.amount);
      const d = toDate((p as any)?.at);
      if (!amount || !d || d.getFullYear() !== year) continue;
      virPrevu[d.getMonth()] += await convertAmountFromEurToCurrency(
        amount,
        displayCurrency,
      );
    }

    const paids = Array.isArray(f?.realFundingPaids) ? f.realFundingPaids : [];
    if (paids.length > 0) {
      for (const p of paids) {
        const amount = toNumber((p as any)?.amount);
        const d = toDate((p as any)?.at);
        if (!amount || !d || d.getFullYear() !== year) continue;
        const storedAmountConverted = readPaymentConvertedAmount(p);
        if (fundingInputIsLocalCurrency && storedAmountConverted) {
          virRecu[d.getMonth()] += storedAmountConverted;
          continue;
        }
        if (effectiveContractRateToEuro) {
          virRecu[d.getMonth()] += amount / effectiveContractRateToEuro;
          continue;
        }
        virRecu[d.getMonth()] += await convertAmountFromEurToCurrency(
          amount,
          displayCurrency,
        );
      }
    } else {
      const receivedExpected = expected.filter((x: any) => x?.status == 2);
      for (const p of receivedExpected) {
        const amount = toNumber((p as any)?.amount);
        const d = toDate((p as any)?.datePaid) ?? toDate((p as any)?.at);
        if (!amount || !d || d.getFullYear() !== year) continue;
        const storedAmountConverted = readPaymentConvertedAmount(p);
        if (fundingInputIsLocalCurrency && storedAmountConverted) {
          virRecu[d.getMonth()] += storedAmountConverted;
          continue;
        }
        if (effectiveContractRateToEuro) {
          virRecu[d.getMonth()] += amount / effectiveContractRateToEuro;
          continue;
        }
        virRecu[d.getMonth()] += await convertAmountFromEurToCurrency(
          amount,
          displayCurrency,
        );
      }
    }
  }

  return { recDisplay: rec, virDisplayPrevu: virPrevu, virDisplay: virRecu };
}

function computeTerrainArrays(
  year: number,
  indicators: ProjectIndicator[],
  projectId: number,
  funderId: number,
) {
  const prev = Array(12).fill(0) as number[];
  const real = Array(12).fill(0) as number[];
  const cert = Array(12).fill(0) as number[];

  for (const ind of indicators || []) {
    const pid = Number(ind?.project?.id);
    if (!pid || pid !== Number(projectId)) continue;
    if (Number(ind.year) !== Number(year)) continue;

    const indFunderId = readIndicatorFunderId(ind);
    if (indFunderId && Number(indFunderId) !== Number(funderId)) continue;

    const mIdx = normalizeMonth(ind.month);
    if (mIdx < 0 || mIdx > 11) continue;

    const val =
      typeof ind.valueDisplay === "number"
        ? ind.valueDisplay
        : toNumber(ind.value);

    if (ind.type === 1) prev[mIdx] += val || 0;
    else if (ind.type === 2) real[mIdx] += val || 0;
    else if (ind.type === 3) cert[mIdx] += val || 0;
  }

  return { indPrevus: prev, indRealises: real, indCertifies: cert };
}

/* =========================
   ✅ Desired Funding (souhaités)
   ========================= */

function readDesiredCurrency(df: any, projectCurrency: any) {
  const c =
    df?.devise ??
    df?.currency ??
    df?.deviseSelected ??
    df?.currencySelected ??
    df?.paidDevise ??
    df?.estimatedDevise ??
    null;

  if (typeof c === "object" && c) return c;
  if (typeof c === "string" && c) return { code: c };
  if (typeof c === "number") return projectCurrency ?? { id: c };

  return projectCurrency ?? null;
}

function readDesiredAmount(df: any) {
  return toNumber(
    df?.amount ??
      df?.value ??
      df?.total ??
      df?.totalAmount ??
      df?.montant ??
      df?.neededAmount ??
      0,
  );
}

function safeConvertDesiredToEur(amount: number, currencyObj: any): number {
  const code = (currencyObj?.code || currencyObj?.currencyCode || "")
    .toString()
    .toUpperCase();
  if (code === "EUR") return amount;

  try {
    return toNumber(convertToEuro(amount, currencyObj));
  } catch {
    return amount;
  }
}

function readDesiredYear(df: any): number | null {
  const y = Number(df?.year ?? df?.annee ?? df?.startYear ?? df?.fromYear);
  return !y || Number.isNaN(y) ? null : y;
}

function readDesiredSingleMonthIndex(df: any, year: number): number {
  const y = readDesiredYear(df);
  const mRaw = df?.month ?? df?.mois ?? df?.monthIndex ?? df?.monthExpected;
  const mIdx = normalizeMonth(mRaw);

  if (y && y !== year) return -1;
  if (mIdx >= 0 && mIdx <= 11) return mIdx;

  const d = toDate(
    df?.at ??
      df?.expectedAt ??
      df?.date ??
      df?.dateAmount ??
      df?.needAt ??
      df?.createdAt ??
      df?.updatedAt ??
      null,
  );
  if (d && d.getFullYear() === year) return d.getMonth();

  return -1;
}

function readDesiredPeriodMonths(df: any, year: number): number[] | null {
  const y = readDesiredYear(df);
  if (y && y !== year) return null;

  const sRaw = df?.startMonth ?? df?.fromMonth ?? df?.monthStart;
  const eRaw = df?.endMonth ?? df?.toMonth ?? df?.monthEnd;

  const s = normalizeMonth(sRaw);
  const e = normalizeMonth(eRaw);

  if (s < 0 || e < 0) return null;
  if (s > e) return null;

  const months: number[] = [];
  for (let i = s; i <= e; i++) months.push(i);
  return months.length ? months : null;
}

function computeDesiredArrays(year: number, projectsRaw: ProjectApi[]) {
  const desired = Array(12).fill(0) as number[];
  let desiredCount = 0;
  let fallbackJanCount = 0;

  for (const p of projectsRaw || []) {
    const projectCurrency = p?.country?.currency;
    const dfs = Array.isArray(p?.desiredFunding) ? p.desiredFunding : [];

    for (const df of dfs) {
      const amountRaw = readDesiredAmount(df);
      if (!amountRaw) continue;

      desiredCount += 1;

      const currencyObj = readDesiredCurrency(df, projectCurrency);
      const eur = safeConvertDesiredToEur(amountRaw, currencyObj);

      const period = readDesiredPeriodMonths(df, year);
      if (period && period.length) {
        const part = eur / period.length;
        for (const m of period) desired[m] += part || 0;
        continue;
      }

      const mIdx = readDesiredSingleMonthIndex(df, year);
      if (mIdx >= 0 && mIdx <= 11) {
        desired[mIdx] += eur || 0;
        continue;
      }

      const y = readDesiredYear(df);
      if (!y || y === year) {
        desired[0] += eur || 0;
        fallbackJanCount += 1;
      }
    }
  }

  return { desiredDisplay: desired, desiredCount, fallbackJanCount };
}

type FunderGroup = {
  funder: Funder;
  projects: Array<{
    projectRaw: ProjectApi;
    id: number;
    name: string;
    fundings: RealFunding[];
  }>;
  desiredDisplay: number[];
  desiredCount: number;
  desiredFallbackJanCount: number;
  recDisplay: number[];
  virDisplayPrevu: number[];
  virDisplay: number[];
  indPrevus: number[];
  indRealises: number[];
  indCertifies: number[];
};

type FunderTrackingFilterKey =
  | "needs"
  | "contracts"
  | "contracts_verdeo"
  | "contracts_internal"
  | "planned"
  | "received"
  | "terrain"
  | "all";

function sumMonthly(values: number[]): number {
  return (values || []).reduce((acc, n) => acc + (Number(n) || 0), 0);
}

function hasFunderNeeds(group: FunderGroup) {
  return group.desiredCount > 0 || sumMonthly(group.desiredDisplay) > 0;
}

function hasFunderContracts(group: FunderGroup) {
  return (
    sumMonthly(group.recDisplay) > 0 ||
    group.projects.some((project) => (project.fundings || []).length > 0)
  );
}

function readFundingIsVerdeo(funding: any): boolean {
  const raw = funding?.verdeo ?? funding?.viaVerdeo ?? funding?.isVerdeo;
  return raw === true || raw === 1 || raw === "1";
}

function readObtainedFundingIsVerdeo(obtainedFunding: any): boolean {
  const raw =
    obtainedFunding?.foundByVerdeo ??
    obtainedFunding?.viaVerdeo ??
    obtainedFunding?.verdeo;
  return raw === true || raw === 1 || raw === "1";
}

function readObtainedFundingDate(obtainedFunding: any): string | Date | null {
  return (
    obtainedFunding?.obtainedDate ??
    obtainedFunding?.date ??
    obtainedFunding?.createdAt ??
    null
  );
}

function readFunderIdFromObtainedFunding(obtainedFunding: any): number | null {
  const financer = obtainedFunding?.financer ?? obtainedFunding?.financier;
  const raw =
    typeof financer === "number"
      ? financer
      : typeof financer?.id !== "undefined"
        ? financer.id
        : null;
  const id = Number(raw);
  return Number.isFinite(id) && id > 0 ? id : null;
}

function hasFunderContractsVerdeo(group: FunderGroup) {
  return group.projects.some((project) =>
    (project.fundings || []).some((funding) => readFundingIsVerdeo(funding)),
  );
}

function hasFunderContractsInternal(group: FunderGroup) {
  return group.projects.some((project) =>
    (project.fundings || []).some((funding) => !readFundingIsVerdeo(funding)),
  );
}

function hasFunderPlannedPayments(group: FunderGroup) {
  return sumMonthly(group.virDisplayPrevu) > 0;
}

function hasFunderReceivedPayments(group: FunderGroup) {
  return sumMonthly(group.virDisplay) > 0;
}

function hasFunderTerrainPayments(group: FunderGroup) {
  return (
    sumMonthly(group.indPrevus) > 0 ||
    sumMonthly(group.indRealises) > 0 ||
    sumMonthly(group.indCertifies) > 0
  );
}

function keepFunderByTrackingFilter(
  filter: FunderTrackingFilterKey,
  group: FunderGroup,
) {
  switch (filter) {
    case "needs":
      return hasFunderNeeds(group);
    case "contracts":
      return hasFunderContracts(group);
    case "contracts_verdeo":
      return hasFunderContractsVerdeo(group);
    case "contracts_internal":
      return hasFunderContractsInternal(group);
    case "planned":
      return hasFunderPlannedPayments(group);
    case "received":
      return hasFunderReceivedPayments(group);
    case "terrain":
      return hasFunderTerrainPayments(group);
    case "all":
    default:
      return true;
  }
}

function collectFunderCalendarEvents(group: FunderGroup): CalendarEventInput[] {
  const events: CalendarEventInput[] = [];
  for (const project of group.projects || []) {
    const projectId = Number(project?.id);
    const funderId = Number(group?.funder?.id);
    const projectName = String(project?.name || `Projet #${projectId || "?"}`);
    const funderName = String(
      group?.funder?.fullname || `Financeur #${funderId || "?"}`,
    );
    const targetId =
      funderId && projectId
        ? `funding-funder-project-${funderId}-${projectId}`
        : funderId
          ? `funding-funder-card-${funderId}`
          : undefined;

    for (const funding of project.fundings || []) {
      const contractDate = readRealFundingContractDateRaw(funding);
      if (contractDate) {
        events.push({
          date: contractDate,
          label: readFundingIsVerdeo(funding)
            ? `${funderName} · ${projectName} · Contrat via Verdeo`
            : `${funderName} · ${projectName} · Contrat interne`,
          targetId,
          projectId,
          funderId,
        });
      }

      for (const paid of normalizeCollectionLike(
        funding?.realFundingExpectedPaids,
      )) {
        if (paid?.at) {
          events.push({
            date: paid.at,
            label: `${funderName} · ${projectName} · Versement prévu`,
            targetId,
            projectId,
            funderId,
          });
        }
        if (paid?.datePaid) {
          events.push({
            date: paid.datePaid,
            label: `${funderName} · ${projectName} · Versement reçu`,
            targetId,
            projectId,
            funderId,
          });
        }
      }
      for (const paid of normalizeCollectionLike(funding?.realFundingPaids)) {
        if (paid?.at) {
          events.push({
            date: paid.at,
            label: `${funderName} · ${projectName} · Versement reçu`,
            targetId,
            projectId,
            funderId,
          });
        }
      }
    }
  }
  return events;
}

function toDayStart(date: Date) {
  return new Date(date.getFullYear(), date.getMonth(), date.getDate());
}

function toDayKey(date: Date) {
  const y = date.getFullYear();
  const m = `${date.getMonth() + 1}`.padStart(2, "0");
  const d = `${date.getDate()}`.padStart(2, "0");
  return `${y}-${m}-${d}`;
}

function isExpectedPaidAlreadyReceived(paid: any) {
  if (Number(paid?.status) === 2) return true;
  if (paid?.datePaid) return true;
  return false;
}

function readProjectPendingVerdeoAllocationEur(project: any): number {
  const directCandidates = [
    project?.fundingTraceability?.verdeoPendingAllocationFsEur,
    project?.fundingTraceability?.pendingVerdeoAllocationFsEur,
    project?.verdeoPendingAllocationFsEur,
    project?.pendingVerdeoAllocationFsEur,
    project?.verdeo_pending_allocation_fs_eur,
  ];
  for (const candidate of directCandidates) {
    const n = Number(candidate);
    if (Number.isFinite(n) && n > 0.5) return n;
  }

  const invoices = normalizeCollectionLike(project?.verdeoFundingInvoiceds);
  if (!invoices.length) return 0;

  const invoicedFsTotalEur = invoices.reduce((sum, invoice) => {
    const fs = Number(invoice?.fs ?? 0);
    return Number.isFinite(fs) && fs > 0 ? sum + fs : sum;
  }, 0);
  if (invoicedFsTotalEur <= 0.5) return 0;

  const obtainedFundings = normalizeCollectionLike(project?.obtainedFunding);
  const allocatedApproxEur = obtainedFundings.reduce((sum, obtainedFunding) => {
    if (!readObtainedFundingIsVerdeo(obtainedFunding)) return sum;
    const eurValue = Number(
      obtainedFunding?.amountEUR ??
        obtainedFunding?.amountEur ??
        obtainedFunding?.amount_eur ??
        obtainedFunding?.amountConverted ??
        obtainedFunding?.amount_converted ??
        0,
    );
    if (Number.isFinite(eurValue) && eurValue > 0) return sum + eurValue;
    return sum;
  }, 0);

  const pending = invoicedFsTotalEur - allocatedApproxEur;
  return pending > 0.5 ? pending : 0;
}

function countFundingsMissingContractDate(fundings: any[]): number {
  return (fundings || []).reduce((sum, funding) => {
    if (readFundingIsVerdeo(funding)) return sum;
    return readRealFundingDateUsesFallback(funding) ? sum + 1 : sum;
  }, 0);
}

function buildMissingContractDateReminderItem(params: {
  projectName: string;
  projectId?: number;
  funderId?: number;
  targetId?: string;
  count: number;
  referenceDate?: Date;
}): FundingReminderItem | null {
  const count = Number(params?.count ?? 0);
  if (!Number.isFinite(count) || count <= 0) return null;

  const day = toDayStart(params?.referenceDate ?? new Date());
  const label = `${params.projectName} · ${
    count > 1
      ? `${count} dates de contrat à renseigner`
      : "Date du contrat à renseigner"
  }`;
  const id = [
    "overdue",
    "missing-contract-date",
    Number.isFinite(Number(params?.funderId))
      ? Number(params?.funderId)
      : "none",
    Number.isFinite(Number(params?.projectId))
      ? Number(params?.projectId)
      : "none",
    toDayKey(day),
  ].join("|");

  return {
    id,
    section: "overdue",
    label,
    date: day,
    targetId: params?.targetId,
    projectId: Number.isFinite(Number(params?.projectId))
      ? Number(params?.projectId)
      : undefined,
    funderId: Number.isFinite(Number(params?.funderId))
      ? Number(params?.funderId)
      : undefined,
  };
}

function collectFunderReminderItems(
  group: FunderGroup,
  referenceDate: Date = new Date(),
): FundingReminderItem[] {
  const items: FundingReminderItem[] = [];
  const seen = new Set<string>();
  const today = toDayStart(referenceDate);
  const upcomingLimit = new Date(today);
  upcomingLimit.setDate(upcomingLimit.getDate() + 7);

  const funderId = Number(group?.funder?.id);
  const funderName = String(
    group?.funder?.fullname || `Financeur #${funderId || "?"}`,
  );

  const pushReminder = (
    section: FundingReminderItem["section"],
    label: string,
    date: Date,
    targetId?: string,
    projectId?: number,
  ) => {
    const day = toDayStart(date);
    const id = [
      section,
      funderId || "none",
      projectId || "none",
      targetId || "none",
      toDayKey(day),
      label,
    ].join("|");
    if (seen.has(id)) return;
    seen.add(id);
    items.push({
      id,
      section,
      label,
      date: day,
      targetId,
      projectId,
      funderId: Number.isFinite(funderId) ? funderId : undefined,
    });
  };

  const classifyContractDate = (dateValue: any) => {
    const date = toDate(dateValue);
    if (!date) return null;
    const day = toDayStart(date);
    const ts = day.getTime();
    if (ts === today.getTime()) return { section: "today" as const, day };
    if (ts > today.getTime() && ts <= upcomingLimit.getTime()) {
      return { section: "upcoming" as const, day };
    }
    return null;
  };

  for (const project of group.projects || []) {
    const projectId = Number(project?.id);
    const projectName = String(project?.name || `Projet #${projectId || "?"}`);
    const pendingVerdeoAllocationEur = readProjectPendingVerdeoAllocationEur(
      project?.projectRaw,
    );
    const targetId =
      funderId && projectId
        ? `funding-funder-project-${funderId}-${projectId}`
        : funderId
          ? `funding-funder-card-${funderId}`
          : undefined;

    if (pendingVerdeoAllocationEur > 0.5) {
      pushReminder(
        "overdue",
        `${funderName} · ${projectName} · Répartition Verdeo à faire`,
        today,
        targetId,
        Number.isFinite(projectId) ? projectId : undefined,
      );
    }

    const missingContractDateCount = countFundingsMissingContractDate(
      project.fundings || [],
    );
    if (missingContractDateCount > 0) {
      pushReminder(
        "overdue",
        `${funderName} · ${projectName} · ${
          missingContractDateCount > 1
            ? `${missingContractDateCount} dates de contrat à renseigner`
            : "Date du contrat à renseigner"
        }`,
        today,
        targetId,
        Number.isFinite(projectId) ? projectId : undefined,
      );
    }

    for (const funding of project.fundings || []) {
      const contract = classifyContractDate(
        readRealFundingContractDateRaw(funding),
      );
      if (contract) {
        pushReminder(
          contract.section,
          readFundingIsVerdeo(funding)
            ? `${funderName} · ${projectName} · Contrat via Verdeo`
            : `${funderName} · ${projectName} · Contrat interne`,
          contract.day,
          targetId,
          Number.isFinite(projectId) ? projectId : undefined,
        );
      }

      for (const paid of normalizeCollectionLike(
        funding?.realFundingExpectedPaids,
      )) {
        if (isExpectedPaidAlreadyReceived(paid)) continue;
        const amount = Number(paid?.amount ?? 0);
        if (!Number.isFinite(amount) || amount <= 0) continue;

        const plannedAt = toDate(paid?.at);
        if (!plannedAt) continue;
        const plannedDay = toDayStart(plannedAt);
        const plannedTs = plannedDay.getTime();
        if (plannedTs < today.getTime()) {
          pushReminder(
            "overdue",
            `${funderName} · ${projectName} · Versement prévu`,
            plannedDay,
            targetId,
            Number.isFinite(projectId) ? projectId : undefined,
          );
          continue;
        }
        if (plannedTs === today.getTime()) {
          pushReminder(
            "today",
            `${funderName} · ${projectName} · Versement prévu`,
            plannedDay,
            targetId,
            Number.isFinite(projectId) ? projectId : undefined,
          );
          continue;
        }
        if (plannedTs <= upcomingLimit.getTime()) {
          pushReminder(
            "upcoming",
            `${funderName} · ${projectName} · Versement prévu`,
            plannedDay,
            targetId,
            Number.isFinite(projectId) ? projectId : undefined,
          );
        }
      }
    }

    for (const obtainedFunding of normalizeCollectionLike(
      project?.projectRaw?.obtainedFunding,
    )) {
      const obtainedFunderId = readFunderIdFromObtainedFunding(obtainedFunding);
      if (obtainedFunderId && funderId && obtainedFunderId !== funderId)
        continue;

      const obtained = classifyContractDate(
        readObtainedFundingDate(obtainedFunding),
      );
      if (!obtained) continue;
      pushReminder(
        obtained.section,
        readObtainedFundingIsVerdeo(obtainedFunding)
          ? `${funderName} · ${projectName} · Contrat via Verdeo`
          : `${funderName} · ${projectName} · Contrat interne`,
        obtained.day,
        targetId,
        Number.isFinite(projectId) ? projectId : undefined,
      );
    }
  }

  return items;
}

function collectReminderItemsFromCalendarEvents(
  events: CalendarEventInput[],
  referenceDate: Date = new Date(),
): FundingReminderItem[] {
  const items: FundingReminderItem[] = [];
  const seen = new Set<string>();
  const today = toDayStart(referenceDate);
  const upcomingLimit = new Date(today);
  upcomingLimit.setDate(upcomingLimit.getDate() + 7);

  for (const event of events || []) {
    if (!event || typeof event !== "object" || !("date" in event)) continue;
    const label = String(event?.label || "").trim();
    if (!label) continue;

    const lowerLabel = label.toLowerCase();
    const isContract = lowerLabel.includes("contrat");
    const isPlanned = lowerLabel.includes("versement prévu");
    if (!isContract && !isPlanned) continue;

    const date = toDate(event.date);
    if (!date) continue;
    const day = toDayStart(date);
    const ts = day.getTime();

    let section: FundingReminderItem["section"] | null = null;
    if (isPlanned) {
      if (ts < today.getTime()) {
        section = "overdue";
      } else if (ts === today.getTime()) {
        section = "today";
      } else if (ts <= upcomingLimit.getTime()) {
        section = "upcoming";
      }
    } else if (ts === today.getTime()) {
      section = "today";
    } else if (ts > today.getTime() && ts <= upcomingLimit.getTime()) {
      section = "upcoming";
    }
    if (!section) continue;

    const targetId = event?.targetId;
    const projectId = Number(event?.projectId);
    const funderId = Number(event?.funderId);

    const id = [
      section,
      Number.isFinite(funderId) ? funderId : "none",
      Number.isFinite(projectId) ? projectId : "none",
      targetId || "none",
      toDayKey(day),
      label,
    ].join("|");
    if (seen.has(id)) continue;
    seen.add(id);

    items.push({
      id,
      section,
      label,
      date: day,
      targetId,
      projectId: Number.isFinite(projectId) ? projectId : undefined,
      funderId: Number.isFinite(funderId) ? funderId : undefined,
    });
  }

  return items;
}

type FunderTrackingSummary = {
  needs: boolean;
  contracts: boolean;
  contractsVerdeo: boolean;
  contractsInternal: boolean;
  planned: boolean;
  received: boolean;
  terrain: boolean;
  calendarEvents: CalendarEventInput[];
  reminderItems: FundingReminderItem[];
};

function hasFunderTrackingFundingSignals(
  summary: FunderTrackingSummary | null | undefined,
): boolean {
  if (!summary) return false;
  return !!(
    summary.needs ||
    summary.contracts ||
    summary.contractsVerdeo ||
    summary.contractsInternal ||
    summary.planned ||
    summary.received ||
    (summary.calendarEvents || []).length > 0 ||
    (summary.reminderItems || []).length > 0
  );
}

function extractProjectsFromByProjectFundingResponse(res: any): any[] {
  const raw = (res as any)?.data ?? res;
  if (Array.isArray(raw)) return raw;
  if (Array.isArray(raw?.data)) return raw.data;
  return [];
}

function readProjectIdFromIndicator(indicator: any): number | null {
  const raw =
    typeof indicator?.project === "number"
      ? indicator.project
      : indicator?.project?.id;
  const id = Number(raw);
  return Number.isFinite(id) && id > 0 ? id : null;
}

function buildFunderTrackingSummaryById(
  fundingRes: any,
  indicatorsRes: any,
): Record<number, FunderTrackingSummary> {
  const byFunderId: Record<number, FunderTrackingSummary> = {};
  const ensure = (funderId: number) => {
    if (!byFunderId[funderId]) {
      byFunderId[funderId] = {
        needs: false,
        contracts: false,
        contractsVerdeo: false,
        contractsInternal: false,
        planned: false,
        received: false,
        terrain: false,
        calendarEvents: [],
        reminderItems: [],
      };
    }
    return byFunderId[funderId];
  };

  const projects = extractProjectsFromByProjectFundingResponse(fundingRes);
  const projectFunderIds = new Map<number, Set<number>>();

  for (const project of projects) {
    const projectId = Number(project?.id);
    if (!projectId || Number.isNaN(projectId)) continue;
    const projectName = String(project?.name || `Projet #${projectId}`);

    const fundings = normalizeCollectionLike(project?.realFundings);
    const obtainedFundings = normalizeCollectionLike(project?.obtainedFunding);
    const involvedFunders = new Set<number>();
    const missingContractDateCountByFunderId = new Map<number, number>();
    for (const funding of fundings) {
      const funderId = readFunderIdFromFunding(funding);
      if (!funderId) continue;
      involvedFunders.add(funderId);

      const summary = ensure(funderId);
      summary.contracts = true;
      if (readFundingIsVerdeo(funding)) {
        summary.contractsVerdeo = true;
      } else {
        summary.contractsInternal = true;
      }
      if (
        !readFundingIsVerdeo(funding) &&
        readRealFundingDateUsesFallback(funding)
      ) {
        missingContractDateCountByFunderId.set(
          funderId,
          Number(missingContractDateCountByFunderId.get(funderId) || 0) + 1,
        );
      }

      const contractDate = readRealFundingContractDateRaw(funding);
      if (contractDate) {
        const targetId = `funding-funder-project-${funderId}-${projectId}`;
        summary.calendarEvents.push({
          date: contractDate,
          label: readFundingIsVerdeo(funding)
            ? `${projectName} · Contrat via Verdeo`
            : `${projectName} · Contrat interne`,
          targetId,
          projectId,
          funderId,
        });
      }

      const expectedPaids = normalizeCollectionLike(
        funding?.realFundingExpectedPaids,
      );
      if (
        expectedPaids.some((paid) => {
          const amount = Number(paid?.amount ?? 0);
          return Number.isFinite(amount) && amount > 0;
        })
      ) {
        summary.planned = true;
      }

      if (
        expectedPaids.some((paid) => {
          const amount = Number(paid?.amount ?? 0);
          return (
            Number(paid?.status) === 2 && Number.isFinite(amount) && amount > 0
          );
        })
      ) {
        summary.received = true;
      }

      for (const paid of expectedPaids) {
        if (paid?.at) {
          const targetId = `funding-funder-project-${funderId}-${projectId}`;
          summary.calendarEvents.push({
            date: paid.at,
            label: `${projectName} · Versement prévu`,
            targetId,
            projectId,
            funderId,
          });
        }
        if (paid?.datePaid) {
          const targetId = `funding-funder-project-${funderId}-${projectId}`;
          summary.calendarEvents.push({
            date: paid.datePaid,
            label: `${projectName} · Versement reçu`,
            targetId,
            projectId,
            funderId,
          });
        }
      }

      const paids = normalizeCollectionLike(funding?.realFundingPaids);
      if (
        paids.some((paid) => {
          const amount = Number(paid?.amount ?? 0);
          return Number.isFinite(amount) && amount > 0;
        })
      ) {
        summary.received = true;
      }
      for (const paid of paids) {
        if (paid?.at) {
          const targetId = `funding-funder-project-${funderId}-${projectId}`;
          summary.calendarEvents.push({
            date: paid.at,
            label: `${projectName} · Versement reçu`,
            targetId,
            projectId,
            funderId,
          });
        }
      }
    }

    for (const obtainedFunding of obtainedFundings) {
      const funderId = readFunderIdFromObtainedFunding(obtainedFunding);
      if (!funderId) continue;
      involvedFunders.add(funderId);

      const summary = ensure(funderId);
      summary.contracts = true;
      if (readObtainedFundingIsVerdeo(obtainedFunding)) {
        summary.contractsVerdeo = true;
      } else {
        summary.contractsInternal = true;
      }

      const obtainedDate = readObtainedFundingDate(obtainedFunding);
      if (obtainedDate) {
        const targetId = `funding-funder-project-${funderId}-${projectId}`;
        summary.calendarEvents.push({
          date: obtainedDate,
          label: readObtainedFundingIsVerdeo(obtainedFunding)
            ? `${projectName} · Contrat via Verdeo`
            : `${projectName} · Contrat interne`,
          targetId,
          projectId,
          funderId,
        });
      }
    }

    projectFunderIds.set(projectId, involvedFunders);
    const hasDesiredNeeds =
      normalizeCollectionLike(project?.desiredFunding).length > 0;
    if (hasDesiredNeeds) {
      for (const funderId of involvedFunders) {
        ensure(funderId).needs = true;
      }
    }
    for (const [
      funderId,
      count,
    ] of missingContractDateCountByFunderId.entries()) {
      const reminder = buildMissingContractDateReminderItem({
        projectName,
        projectId,
        funderId,
        targetId: `funding-funder-project-${funderId}-${projectId}`,
        count,
      });
      if (reminder) ensure(funderId).reminderItems.push(reminder);
    }
  }

  const indicators = Array.isArray(indicatorsRes)
    ? indicatorsRes
    : Array.isArray((indicatorsRes as any)?.data)
      ? (indicatorsRes as any).data
      : [];

  for (const indicator of indicators) {
    const funderId = readIndicatorFunderId(indicator);
    if (!funderId) continue;

    const projectId = readProjectIdFromIndicator(indicator);
    if (projectId && projectFunderIds.has(projectId)) {
      const involved = projectFunderIds.get(projectId)!;
      if (!involved.has(funderId)) continue;
    }

    ensure(funderId).terrain = true;
  }

  return byFunderId;
}

function mergeFunderTrackingSummaryMaps(
  base: Record<number, FunderTrackingSummary>,
  incoming: Record<number, FunderTrackingSummary>,
) {
  const dedupeReminderItems = (items: FundingReminderItem[]) => {
    return Array.from(
      new Map(
        (items || []).map((item) => [
          [
            item?.id || "",
            item?.section || "",
            String(item?.label || "").trim(),
            toDayKey(toDate(item?.date) || new Date(0)) || "",
            item?.targetId || "",
            Number(item?.projectId) || 0,
            Number(item?.funderId) || 0,
          ].join("|"),
          item,
        ]),
      ).values(),
    );
  };

  const merged: Record<number, FunderTrackingSummary> = { ...(base || {}) };
  for (const [funderIdRaw, summary] of Object.entries(incoming || {})) {
    const funderId = Number(funderIdRaw);
    if (!funderId || Number.isNaN(funderId) || !summary) continue;
    const prev = merged[funderId];
    if (!prev) {
      merged[funderId] = {
        ...summary,
        calendarEvents: [...summary.calendarEvents],
        reminderItems: dedupeReminderItems([...(summary.reminderItems || [])]),
      };
      continue;
    }
    merged[funderId] = {
      needs: prev.needs || summary.needs,
      contracts: prev.contracts || summary.contracts,
      contractsVerdeo: prev.contractsVerdeo || summary.contractsVerdeo,
      contractsInternal: prev.contractsInternal || summary.contractsInternal,
      planned: prev.planned || summary.planned,
      received: prev.received || summary.received,
      terrain: prev.terrain || summary.terrain,
      calendarEvents: [
        ...(prev.calendarEvents || []),
        ...(summary.calendarEvents || []),
      ],
      reminderItems: dedupeReminderItems([
        ...(prev.reminderItems || []),
        ...(summary.reminderItems || []),
      ]),
    };
  }
  return merged;
}

function buildFunderTrackingSummaryFromProjects(
  projects: ProjectApi[],
  funderId?: number,
): FunderTrackingSummary {
  const summary: FunderTrackingSummary = {
    needs: false,
    contracts: false,
    contractsVerdeo: false,
    contractsInternal: false,
    planned: false,
    received: false,
    terrain: false,
    calendarEvents: [],
    reminderItems: [],
  };

  for (const project of projects || []) {
    const projectId = Number(project?.id);
    const projectName = String(project?.name || `Projet #${projectId || "?"}`);
    const targetId =
      funderId && projectId
        ? `funding-funder-project-${funderId}-${projectId}`
        : funderId
          ? `funding-funder-card-${funderId}`
          : undefined;
    if (normalizeCollectionLike(project?.desiredFunding).length > 0) {
      summary.needs = true;
    }
    let missingContractDateCount = 0;
    for (const funding of normalizeCollectionLike(project?.realFundings)) {
      summary.contracts = true;
      if (readFundingIsVerdeo(funding)) {
        summary.contractsVerdeo = true;
      } else {
        summary.contractsInternal = true;
      }
      if (
        !readFundingIsVerdeo(funding) &&
        readRealFundingDateUsesFallback(funding)
      ) {
        missingContractDateCount += 1;
      }

      const contractDate = readRealFundingContractDateRaw(funding);
      if (contractDate) {
        summary.calendarEvents.push({
          date: contractDate,
          label: readFundingIsVerdeo(funding)
            ? `${projectName} · Contrat via Verdeo`
            : `${projectName} · Contrat interne`,
          targetId,
          projectId: Number.isFinite(projectId) ? projectId : undefined,
          funderId,
        });
      }

      const expectedPaids = normalizeCollectionLike(
        funding?.realFundingExpectedPaids,
      );
      if (
        expectedPaids.some((paid) => {
          const amount = Number(paid?.amount ?? 0);
          return Number.isFinite(amount) && amount > 0;
        })
      ) {
        summary.planned = true;
      }
      if (
        expectedPaids.some((paid) => {
          const amount = Number(paid?.amount ?? 0);
          return (
            Number(paid?.status) === 2 && Number.isFinite(amount) && amount > 0
          );
        })
      ) {
        summary.received = true;
      }
      for (const paid of expectedPaids) {
        if (paid?.at) {
          summary.calendarEvents.push({
            date: paid.at,
            label: `${projectName} · Versement prévu`,
            targetId,
            projectId: Number.isFinite(projectId) ? projectId : undefined,
            funderId,
          });
        }
        if (paid?.datePaid) {
          summary.calendarEvents.push({
            date: paid.datePaid,
            label: `${projectName} · Versement reçu`,
            targetId,
            projectId: Number.isFinite(projectId) ? projectId : undefined,
            funderId,
          });
        }
      }

      const paids = normalizeCollectionLike(funding?.realFundingPaids);
      if (
        paids.some((paid) => {
          const amount = Number(paid?.amount ?? 0);
          return Number.isFinite(amount) && amount > 0;
        })
      ) {
        summary.received = true;
      }
      for (const paid of paids) {
        if (paid?.at) {
          summary.calendarEvents.push({
            date: paid.at,
            label: `${projectName} · Versement reçu`,
            targetId,
            projectId: Number.isFinite(projectId) ? projectId : undefined,
            funderId,
          });
        }
      }
    }

    const missingDateReminder = buildMissingContractDateReminderItem({
      projectName,
      projectId: Number.isFinite(projectId) ? projectId : undefined,
      funderId,
      targetId,
      count: missingContractDateCount,
    });
    if (missingDateReminder) {
      summary.reminderItems.push(missingDateReminder);
    }

    for (const obtainedFunding of normalizeCollectionLike(
      project?.obtainedFunding,
    )) {
      summary.contracts = true;
      if (readObtainedFundingIsVerdeo(obtainedFunding)) {
        summary.contractsVerdeo = true;
      } else {
        summary.contractsInternal = true;
      }
      const obtainedDate = readObtainedFundingDate(obtainedFunding);
      if (obtainedDate) {
        summary.calendarEvents.push({
          date: obtainedDate,
          label: readObtainedFundingIsVerdeo(obtainedFunding)
            ? `${projectName} · Contrat via Verdeo`
            : `${projectName} · Contrat interne`,
          targetId,
          projectId: Number.isFinite(projectId) ? projectId : undefined,
          funderId,
        });
      }
    }
  }

  return summary;
}

function normalizeFundingsForUI(
  list: RealFunding[],
  fallbackFunder?: {
    id: number;
    fullname?: string;
    structureName?: string;
    name?: string;
  } | null,
) {
  const fallback =
    fallbackFunder && Number(fallbackFunder.id) > 0 ? fallbackFunder : null;

  return (list || []).map((rf) => ({
    ...rf,
    financier: rf?.financier ?? rf?.financer ?? fallback ?? undefined,
    financer: rf?.financer ?? rf?.financier ?? fallback ?? undefined,
    representant: rf?.verdeo
      ? { firstname: "Verdeo", lastname: "" }
      : rf?.representant,
  }));
}

function normalizeProjectsForUI(list: ProjectApi[]) {
  return (list || []).map((p) => ({
    ...p,
    realFundings: normalizeFundingsForUI(p.realFundings || []),
  }));
}

function extractRealFundings(res: any): RealFunding[] {
  if (Array.isArray(res)) return res as RealFunding[];
  if (Array.isArray(res?.data)) return res.data as RealFunding[];
  if (Array.isArray(res?.realFundings))
    return res.realFundings as RealFunding[];
  if (Array.isArray(res?.fundings)) return res.fundings as RealFunding[];
  if (res && typeof res === "object" && Array.isArray(res?.realFundings))
    return res.realFundings as RealFunding[];
  return [];
}

function extractProjectsFromFunderFundingResponse(
  res: any,
  funderId: number,
): ProjectApi[] {
  const asArray = (v: any) => (Array.isArray(v) ? v : []);
  const funderCandidates = asArray(res).concat(asArray(res?.data));

  const target =
    funderCandidates.find((f: any) => Number(f?.id) === Number(funderId)) ??
    funderCandidates[0];
  const fallbackFunder = {
    id: Number(target?.id) || Number(funderId),
    fullname: target?.fullname,
    structureName: target?.structureName,
    name: target?.name,
  };

  const fundings = normalizeFundingsForUI(
    asArray(target?.realFundings) as RealFunding[],
    fallbackFunder,
  );

  const byProject = new Map<number, ProjectApi>();
  for (const rf of fundings) {
    const project = rf?.project;
    const pid = Number(project?.id);
    if (!pid || Number.isNaN(pid)) continue;

    if (!byProject.has(pid)) {
      byProject.set(pid, {
        ...(project as any),
        id: pid,
        realFundings: [],
        desiredFunding: Array.isArray((project as any)?.desiredFunding)
          ? (project as any).desiredFunding
          : [],
      });
    }

    byProject.get(pid)!.realFundings!.push(rf);
  }

  return Array.from(byProject.values()).sort((a, b) =>
    String(a?.name || "").localeCompare(String(b?.name || ""), "fr"),
  );
}

/* =========================
   ✅ Modal: choisir programme → projet puis rediriger
   ========================= */

function AddFundingGlobalModal({
  open,
  onOpenChange,
  year,
  funder,
  projectsToSale,
  loadingProjectsToSale,
  blockedProjectIds,
}: {
  open: boolean;
  onOpenChange: (v: boolean) => void;
  year: number;
  funder: Funder | null;
  projectsToSale: SaleProject[];
  loadingProjectsToSale: boolean;
  blockedProjectIds: number[];
}) {
  const blocked = useMemo(
    () => new Set((blockedProjectIds || []).map((x) => Number(x))),
    [blockedProjectIds],
  );

  const programs = useMemo(() => {
    const map = new Map<number, SaleProgram>();
    for (const p of projectsToSale || []) {
      const prog = p?.program;
      if (!prog?.id) continue;
      map.set(Number(prog.id), { id: Number(prog.id), name: prog.name || "" });
    }
    return Array.from(map.values()).sort((a, b) =>
      (a.name || "").localeCompare(b.name || "", "fr"),
    );
  }, [projectsToSale]);

  const [programId, setProgramId] = useState<string>("");
  const [projectId, setProjectId] = useState<string>("");
  const [search, setSearch] = useState("");

  useEffect(() => {
    if (!open) return;
    setProgramId("");
    setProjectId("");
    setSearch("");
  }, [open]);

  const projectsForProgram = useMemo(() => {
    const pid = Number(programId);
    const list = (projectsToSale || [])
      .filter((p) => Number(p?.program?.id) === pid)
      .sort((a, b) => (a.name || "").localeCompare(b.name || "", "fr"));

    const q = (search || "").trim().toLowerCase();
    if (!q) return list;
    return list.filter((p) => (p.name || "").toLowerCase().includes(q));
  }, [projectsToSale, programId, search]);

  const selectedProject = useMemo(() => {
    const id = Number(projectId);
    if (!id) return null;
    return (projectsToSale || []).find((p) => Number(p.id) === id) || null;
  }, [projectId, projectsToSale]);

  const confirmDisabled =
    loadingProjectsToSale ||
    !funder ||
    !Number(programId) ||
    !Number(projectId) ||
    blocked.has(Number(projectId));

  const handleConfirm = () => {
    if (confirmDisabled) return;
    const pid = Number(projectId);
    const fid = Number(funder?.id);

    const url = new URL(`${window.location.origin}/dashboard/project/${pid}`);
    url.searchParams.set("tab", "financement");
    url.searchParams.set("year", String(year));
    url.searchParams.set("openAddFunding", "true");
    // Ouvre le parcours "souhaité -> obtenu" pour proposer la répartition FS dès la création.
    url.searchParams.set("openDesiredFunding", "true");
    url.searchParams.set("idFunder", String(fid)); // ✅ auto-sélection financeur côté "par projet"
    window.location.href = url.toString();
  };

  const blockedInThisProgram = useMemo(() => {
    const ids = projectsForProgram.map((p) => Number(p.id));
    return ids.filter((id) => blocked.has(id)).length;
  }, [projectsForProgram, blocked]);

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="sm:max-w-[720px]">
        <DialogHeader>
          <DialogTitle className="text-base">
            Ajouter un financement —{" "}
            <span className="font-semibold text-slate-900">
              {funder?.fullname || "Financeur"}
            </span>
          </DialogTitle>
          <DialogDescription className="text-sm">
            Choisis d’abord un <span className="font-medium">programme</span>,
            puis un <span className="font-medium">projet</span>. À validation,
            tu seras redirigé vers l’onglet{" "}
            <span className="font-medium">Financement</span> du projet
            (financeur pré-sélectionné).
          </DialogDescription>
        </DialogHeader>

        <div className="mt-2 grid grid-cols-1 gap-4">
          {loadingProjectsToSale && (
            <div className="inline-flex w-fit items-center gap-2 rounded-full bg-white px-3 py-1 text-[11px] font-medium text-slate-600 ring-1 ring-slate-200">
              <Loader2 className="h-3.5 w-3.5 animate-spin" />
              Chargement des programmes et projets...
            </div>
          )}

          {/* Étape 1 - Programme */}
          <div className="rounded-2xl border bg-white p-4">
            <div className="flex items-start justify-between gap-3">
              <div>
                <div className="text-sm font-semibold text-slate-900">
                  Étape 1 — Sélectionner un programme
                </div>
                <div className="text-[12px] text-muted-foreground mt-1">
                  Les projets sont regroupés par programme pour t’aider à
                  retrouver rapidement le bon projet.
                </div>
              </div>
              <div className="text-[11px] text-muted-foreground">
                {programs.length} programme(s)
              </div>
            </div>

            <div className="mt-3">
              <Select
                value={programId}
                onValueChange={(v) => {
                  setProgramId(v);
                  setProjectId("");
                  setSearch("");
                }}
                disabled={loadingProjectsToSale}
              >
                <SelectTrigger className="w-full">
                  <SelectValue placeholder="Choisir un programme…" />
                </SelectTrigger>
                <SelectContent>
                  {programs.map((p) => (
                    <SelectItem key={p.id} value={String(p.id)}>
                      {p.name || `Programme #${p.id}`}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
          </div>

          {/* Étape 2 - Projet */}
          <div className="rounded-2xl border bg-white p-4">
            <div className="flex items-start justify-between gap-3">
              <div>
                <div className="text-sm font-semibold text-slate-900">
                  Étape 2 — Sélectionner un projet
                </div>
                <div className="text-[12px] text-muted-foreground mt-1">
                  Les projets déjà présents dans les contrats de ce financeur
                  sont <span className="font-medium">désactivés</span>.
                </div>
              </div>

              {Number(programId) ? (
                <div className="text-[11px] text-muted-foreground">
                  {projectsForProgram.length} projet(s)
                  {blockedInThisProgram ? (
                    <span>{` • ${blockedInThisProgram} déjà présents`}</span>
                  ) : null}
                </div>
              ) : (
                <div className="text-[11px] text-muted-foreground">
                  Choisis un programme
                </div>
              )}
            </div>

            <div className="mt-3 grid grid-cols-1 gap-3">
              <Input
                value={search}
                onChange={(e) => setSearch(e.target.value)}
                placeholder="Rechercher un projet…"
                disabled={!Number(programId) || loadingProjectsToSale}
              />

              <Select
                value={projectId}
                onValueChange={(v) => setProjectId(v)}
                disabled={!Number(programId) || loadingProjectsToSale}
              >
                <SelectTrigger className="w-full">
                  <SelectValue
                    placeholder={
                      Number(programId)
                        ? "Choisir un projet…"
                        : "Choisir un programme d’abord…"
                    }
                  />
                </SelectTrigger>
                <SelectContent>
                  {projectsForProgram.map((p) => {
                    const isBlocked = blocked.has(Number(p.id));
                    const label = p.name || `Projet #${p.id}`;
                    return (
                      <SelectItem
                        key={p.id}
                        value={String(p.id)}
                        disabled={isBlocked}
                      >
                        {label}
                        {isBlocked ? " — déjà présent" : ""}
                      </SelectItem>
                    );
                  })}

                  {Number(programId) && projectsForProgram.length === 0 ? (
                    <div className="px-3 py-2 text-sm text-muted-foreground">
                      Aucun projet trouvé pour ce programme (ou filtre trop
                      restrictif).
                    </div>
                  ) : null}
                </SelectContent>
              </Select>

              {selectedProject ? (
                <div className="rounded-xl border bg-slate-50 p-3 text-[12px] text-slate-700">
                  <div className="font-medium text-slate-900">
                    Sélection : {selectedProject.name}
                  </div>
                  <div className="text-muted-foreground">
                    Programme : {selectedProject.program?.name || "—"}
                  </div>
                </div>
              ) : null}
            </div>
          </div>

          {/* Indication */}
          <div className="rounded-2xl bg-[#F7F9FB] p-4 text-[12px] text-slate-700">
            <div className="font-medium text-slate-900">Bon à savoir</div>
            <ul className="mt-2 list-disc pl-5 space-y-1 text-muted-foreground">
              <li>
                Ce bouton ne crée pas le contrat directement : il t’emmène sur
                le projet et ouvre l’ajout de financement.
              </li>
              <li>
                Le financeur{" "}
                <span className="font-medium">sera pré-sélectionné</span> via
                l’URL (<code>idFunder</code>).
              </li>
            </ul>
          </div>
        </div>

        <DialogFooter className="mt-2">
          <Button
            type="button"
            variant="outline"
            onClick={() => onOpenChange(false)}
          >
            Annuler
          </Button>

          <Button
            type="button"
            onClick={handleConfirm}
            disabled={confirmDisabled}
          >
            Aller au financement
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

function ProjectDetailCard({
  year,
  project,
  funderId,
  funders,
  setFunders,
  onProjectFundingsUpdated,
  onlyOneFunder,
  forceEuroDisplay,
  cardDomId,
}: {
  year: number;
  funderId: number;
  project: FunderGroup["projects"][number];
  funders: Funder[];
  setFunders: Dispatch<SetStateAction<Funder[]>>;
  onProjectFundingsUpdated: (
    funderId: number,
    projectId: number,
    apiResult: any,
  ) => void;
  onlyOneFunder: boolean;
  forceEuroDisplay: boolean;
  cardDomId?: string;
}) {
  const [open, setOpen] = useState(false);

  const totalContract = useMemo(() => {
    return (project?.fundings || []).reduce(
      (s, f) => s + toNumber(f?.paidAmountEUR ?? f?.paidAmount),
      0,
    );
  }, [project?.fundings]);

  const projectCurrency = useMemo(() => {
    const fromProject = project?.projectRaw?.country?.currency;
    if (fromProject?.code) return fromProject;

    const fromFundingProject = (project?.fundings || [])
      .map((f: any) => f?.project?.country?.currency)
      .find((c: any) => c && typeof c === "object" && c?.code);
    if (fromFundingProject?.code) return fromFundingProject;

    const fromPaidDevise = (project?.fundings || [])
      .map((f: any) => f?.paidDevise ?? f?.paid_devise)
      .find(
        (d: any) =>
          d &&
          typeof d === "object" &&
          d?.code &&
          String(d.code).toUpperCase() !== "EUR",
      );
    if (fromPaidDevise?.code) return fromPaidDevise;

    return fromProject ?? null;
  }, [project?.projectRaw?.country?.currency, project?.fundings]);
  const projectCurrencyCode =
    typeof projectCurrency?.code === "string"
      ? String(projectCurrency.code).toUpperCase()
      : null;
  const inferredLocalCurrencyCode = useMemo(() => {
    return (
      (project?.fundings || [])
        .map((f: any) => readFundingCurrencyCode(f))
        .find((code: string | null) => !!code && code !== "EUR") ?? null
    );
  }, [project?.fundings]);
  const hasLocalConvertibleContract = useMemo(() => {
    return (project?.fundings || []).some((f: any) => {
      const converted = readFundingConvertedAmount(f);
      const hasProjectCurrency =
        !!f?.project?.country?.currency?.code &&
        String(f.project.country.currency.code).toUpperCase() !== "EUR";
      const hasContractDate = !!(f?.dateAmount ?? f?.date_amount);
      return !!converted || (hasProjectCurrency && hasContractDate);
    });
  }, [project?.fundings]);
  const localCurrencyCode =
    (projectCurrencyCode && projectCurrencyCode !== "EUR"
      ? projectCurrencyCode
      : null) ??
    inferredLocalCurrencyCode ??
    (hasLocalConvertibleContract ? "LOCAL" : null);
  const [currencySelected, setCurrencySelected] = useState<string>(
    forceEuroDisplay ? "EUR" : (localCurrencyCode ?? "EUR"),
  );
  useEffect(() => {
    setCurrencySelected(
      forceEuroDisplay ? "EUR" : (localCurrencyCode ?? "EUR"),
    );
  }, [forceEuroDisplay, localCurrencyCode]);
  const inferredLocalCurrencySymbol = useMemo(() => {
    return (
      (project?.fundings || [])
        .map((f: any) => readFundingCurrencySymbol(f))
        .find((symbol: string | null) => !!symbol) ?? null
    );
  }, [project?.fundings]);
  const projectCurrencySymbol =
    projectCurrency?.symbol ||
    inferredLocalCurrencySymbol ||
    (localCurrencyCode && localCurrencyCode !== "EUR" ? localCurrencyCode : "");
  const projectForRows = useMemo(
    () => ({
      ...project.projectRaw,
      country: {
        ...(project?.projectRaw?.country || {}),
        currency:
          projectCurrency ??
          project?.projectRaw?.country?.currency ??
          (localCurrencyCode
            ? {
                code: localCurrencyCode,
                symbol: projectCurrencySymbol || localCurrencyCode,
              }
            : null),
      },
    }),
    [
      project?.projectRaw,
      projectCurrency,
      localCurrencyCode,
      projectCurrencySymbol,
    ],
  );

  return (
    <div id={cardDomId} className="rounded-2xl border bg-white scroll-mt-20">
      <button
        type="button"
        onClick={() => setOpen((v) => !v)}
        className="w-full flex items-center justify-between gap-3 px-4 py-3"
      >
        <div className="min-w-0 text-left">
          <a
            href={`/dashboard/project/${project.id}`}
            className={cn(
              "text-sm font-semibold text-slate-900 hover:underline",
              "inline-flex items-center gap-2",
            )}
            onClick={(e) => e.stopPropagation()}
          >
            <ExternalLink className="w-4 h-4" />
            <span className="truncate">{project.name}</span>
          </a>

          <div className="mt-1 text-[11px] text-muted-foreground flex flex-wrap gap-x-3 gap-y-1">
            <span>{project.fundings?.length || 0} contrat(s)</span>
            <span>•</span>
            <span className="inline-flex items-center gap-1">
              Total contrats: {formatEUR(totalContract)} €
              <Popover>
                <PopoverTrigger asChild>
                  <Info className="w-3.5 h-3.5 cursor-pointer" />
                </PopoverTrigger>
                <PopoverContent className="z-[99999] text-xs w-[300px] space-y-2">
                  <p className="m-0 font-semibold text-foreground">
                    Valorisation des contrats
                  </p>
                  <p className="m-0">
                    Ce total regroupe les contrats en euros, avec valorisation
                    au taux de la date de chaque contrat.
                  </p>
                  <p className="m-0">
                    Si plusieurs contrats ont des dates différentes, le total
                    combine des taux historiques différents (taux mixtes).
                  </p>
                </PopoverContent>
              </Popover>
            </span>
          </div>
        </div>

        <div className="shrink-0 flex items-center gap-2">
          <Button
            type="button"
            variant="outline"
            className="hidden md:inline-flex"
            onClick={(e) => {
              e.stopPropagation();
              const url = new URL(
                `${window.location.origin}/dashboard/project/${project.id}`,
              );
              url.searchParams.set("tab", "financement");
              url.searchParams.set("year", String(year));
              url.searchParams.set("openAddFunding", "true");
              // Ouvre le parcours "souhaité -> obtenu" pour proposer la répartition FS dès la création.
              url.searchParams.set("openDesiredFunding", "true");
              url.searchParams.set("idFunder", String(funderId));
              window.location.href = url.toString();
            }}
          >
            <Plus className="w-4 h-4 mr-2" />
            Ajouter un financement
          </Button>

          {open ? (
            <ChevronDown className="w-5 h-5" />
          ) : (
            <ChevronRight className="w-5 h-5" />
          )}
        </div>
      </button>

      {open && (
        <div className="px-4 pb-4">
          {!forceEuroDisplay && localCurrencyCode ? (
            <div className="mt-2 flex justify-end">
              <Select
                value={currencySelected}
                onValueChange={setCurrencySelected}
              >
                <SelectTrigger className="h-8 w-[120px]">
                  <SelectValue placeholder="Devise" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="EUR">€</SelectItem>
                  <SelectItem value={localCurrencyCode}>
                    {projectCurrency?.symbol || localCurrencyCode}
                  </SelectItem>
                </SelectContent>
              </Select>
            </div>
          ) : null}

          <div className="mt-2 space-y-3">
            {(project.fundings || []).map((funding) => (
              <FundingRow
                key={funding.id}
                funding={funding as any}
                project={projectForRows as any}
                currencySelected={currencySelected as any}
                projectCurrencySymbol={projectCurrencySymbol}
                funders={funders as any}
                setFunders={setFunders as any}
                onUpdated={(apiResult: any) =>
                  onProjectFundingsUpdated(funderId, project.id, apiResult)
                }
                onDeleted={(apiResult: any) =>
                  onProjectFundingsUpdated(funderId, project.id, apiResult)
                }
              />
            ))}

            {(project.fundings || []).length === 0 ? (
              <div className="rounded-xl border bg-white p-4 text-sm text-muted-foreground">
                Aucun contrat pour ce projet et ce financeur.
              </div>
            ) : null}

            {onlyOneFunder ? (
              <div className="pt-1 text-[11px] text-muted-foreground">
                Astuce : pour créer un nouveau contrat, utilise “Ajouter un
                financement” (ça ouvre le projet sur l’onglet financement).
              </div>
            ) : null}
          </div>
        </div>
      )}
    </div>
  );
}

function GroupContent({
  year,
  group,
  funders,
  setFunders,
  onProjectFundingsUpdated,
  indicatorsLoading,
  onlyOneFunder,
  onOpenAddFundingGlobal,
  forceEuroDisplay,
}: {
  year: number;
  group: FunderGroup;
  funders: Funder[];
  setFunders: Dispatch<SetStateAction<Funder[]>>;
  onProjectFundingsUpdated: (
    funderId: number,
    projectId: number,
    apiResult: any,
  ) => void;
  indicatorsLoading: boolean;
  onlyOneFunder: boolean;
  onOpenAddFundingGlobal: (payload: {
    funder: Funder;
    blockedProjectIds: number[];
  }) => void;
  forceEuroDisplay: boolean;
}) {
  const {
    code: localCurrencyCode,
    symbol: localCurrencySymbol,
    currency: localCurrency,
  } = useMemo(() => inferGroupLocalCurrency(group), [group]);
  const [currencySelected, setCurrencySelected] = useState<string>(
    forceEuroDisplay ? "EUR" : (localCurrencyCode ?? "EUR"),
  );
  const [isConvertingCurrency, setIsConvertingCurrency] = useState(false);
  const baseDisplayArrays = useMemo<GroupDisplayArrays>(
    () => ({
      desiredDisplay: [...group.desiredDisplay],
      recDisplay: [...group.recDisplay],
      virDisplayPrevu: [...group.virDisplayPrevu],
      virDisplay: [...group.virDisplay],
      indPrevus: [...group.indPrevus],
      indRealises: [...group.indRealises],
      indCertifies: [...group.indCertifies],
    }),
    [
      group.desiredDisplay,
      group.recDisplay,
      group.virDisplayPrevu,
      group.virDisplay,
      group.indPrevus,
      group.indRealises,
      group.indCertifies,
    ],
  );
  const [displayArrays, setDisplayArrays] =
    useState<GroupDisplayArrays>(baseDisplayArrays);

  useEffect(() => {
    setCurrencySelected(
      forceEuroDisplay ? "EUR" : (localCurrencyCode ?? "EUR"),
    );
  }, [forceEuroDisplay, localCurrencyCode]);

  useEffect(() => {
    setDisplayArrays(baseDisplayArrays);
  }, [baseDisplayArrays]);

  useEffect(() => {
    let cancelled = false;

    const buildDisplayArrays = async () => {
      if (
        forceEuroDisplay ||
        currencySelected === "EUR" ||
        !localCurrencyCode ||
        !localCurrency
      ) {
        if (!cancelled) {
          setDisplayArrays(baseDisplayArrays);
          setIsConvertingCurrency(false);
        }
        return;
      }

      if (!cancelled) setIsConvertingCurrency(true);
      try {
        const allFundings = group.projects.flatMap((p) =>
          Array.isArray(p.fundings) ? p.fundings : [],
        );

        const [
          fundingArrays,
          desiredDisplay,
          indPrevus,
          indRealises,
          indCertifies,
        ] = await Promise.all([
          computeFundingArraysForDisplayCurrency(
            year,
            allFundings,
            currencySelected,
            localCurrency,
          ),
          convertMonthlyArrayFromEurToCurrency(
            group.desiredDisplay,
            localCurrency,
          ),
          convertMonthlyArrayFromEurToCurrency(group.indPrevus, localCurrency),
          convertMonthlyArrayFromEurToCurrency(
            group.indRealises,
            localCurrency,
          ),
          convertMonthlyArrayFromEurToCurrency(
            group.indCertifies,
            localCurrency,
          ),
        ]);

        if (cancelled) return;

        setDisplayArrays({
          desiredDisplay,
          recDisplay: fundingArrays.recDisplay,
          virDisplayPrevu: fundingArrays.virDisplayPrevu,
          virDisplay: fundingArrays.virDisplay,
          indPrevus,
          indRealises,
          indCertifies,
        });
      } finally {
        if (!cancelled) setIsConvertingCurrency(false);
      }
    };

    void buildDisplayArrays();
    return () => {
      cancelled = true;
    };
  }, [
    baseDisplayArrays,
    currencySelected,
    forceEuroDisplay,
    group.desiredDisplay,
    group.indCertifies,
    group.indPrevus,
    group.indRealises,
    group.projects,
    localCurrency,
    localCurrencyCode,
    year,
  ]);

  return (
    <>
      <div
        className="flex items-center gap-3"
        data-tour-funding-funder-summary="true"
      >
        <div className="inline-flex items-center gap-2 rounded-full bg-white px-3 py-1 text-xs font-semibold text-slate-700 shadow-sm ring-1 ring-slate-200">
          <span className="h-1.5 w-1.5 rounded-full bg-slate-400" />
          Vue globale
        </div>
        <div className="h-px flex-1 bg-slate-200" />
        {!forceEuroDisplay && localCurrencyCode ? (
          <Select value={currencySelected} onValueChange={setCurrencySelected}>
            <SelectTrigger className="h-8 w-[120px]">
              <SelectValue placeholder="Devise" />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="EUR">€</SelectItem>
              <SelectItem value={localCurrencyCode}>
                {localCurrencySymbol || localCurrencyCode}
              </SelectItem>
            </SelectContent>
          </Select>
        ) : null}
      </div>

      <div className="mt-3 mb-4 space-y-6">
        <div className="rounded-3xl bg-white px-4 py-4 ring-1 ring-slate-200">
          {isConvertingCurrency && (
            <div className="mb-3 inline-flex items-center gap-2 rounded-full bg-white px-3 py-1 text-[11px] font-medium text-slate-600 ring-1 ring-slate-200">
              <Loader2 className="h-3.5 w-3.5 animate-spin" />
              Conversion en cours ({currencySelected})
            </div>
          )}
          {/* ===== souhaités + CTA global ===== */}
          <div className="flex items-center gap-3 w-full">
            <div className="w-1/6 text-sm font-medium text-[#586071]">
              <div className="flex items-start justify-between gap-2">
                <div>
                  <div>Financements souhaités</div>
                  <div className="text-[11px] text-muted-foreground mt-1">
                    {group.desiredCount} besoin(s) • affichage{" "}
                    {currencySelected === "EUR"
                      ? "EUR"
                      : (localCurrencyCode ?? "EUR")}
                  </div>
                </div>

                <span
                  className="shrink-0 inline-flex items-center gap-1 text-blue-700 underline cursor-pointer"
                  onClick={() =>
                    onOpenAddFundingGlobal({
                      funder: group.funder,
                      blockedProjectIds: group.projects.map((p) =>
                        Number(p.id),
                      ),
                    })
                  }
                  title="Choisir un projet puis ouvrir l’ajout de financement (financeur pré-sélectionné)"
                >
                  <Dot className="w-4 h-4" />
                  J’ai trouvé un financement
                </span>
              </div>
            </div>

            <div className="flex w-5/6 flex-wrap gap-2">
              {displayArrays.desiredDisplay.map((v, i) => (
                <FieldInput
                  key={`funder-${group.funder.id}-desired-${year}-${i}`}
                  type={4}
                  defaultValue={v || 0}
                  valueMultiple={v || 0}
                  data={{}}
                />
              ))}
            </div>
          </div>

          {/* ===== contrats signés ===== */}
          <div className="mt-4 flex items-center gap-3 w-full">
            <div className="w-1/6 text-sm font-medium text-[#586071]">
              Financement obtenu (Contrat signé)
            </div>

            <div className="flex w-5/6 flex-wrap gap-2">
              {displayArrays.recDisplay.map((v, i) => (
                <FieldInput
                  key={`funder-${group.funder.id}-rec-${year}-${i}`}
                  type={2}
                  defaultValue={v || 0}
                  valueMultiple={v || 0}
                  data={{}}
                />
              ))}
            </div>
          </div>
        </div>

        <div className="rounded-3xl bg-[#F3FAF3] px-4 py-4">
          <div className="flex items-center gap-3 mb-3 w-full">
            <div className="w-1/6 text-sm font-medium text-[#586071]">
              Versements prévus
            </div>
            <div className="flex w-5/6 flex-wrap gap-2">
              {displayArrays.virDisplayPrevu.map((v, i) => (
                <FieldInput
                  key={`funder-${group.funder.id}-vir-prevu-${year}-${i}`}
                  type={5}
                  defaultValue={v || 0}
                  valueMultiple={v || 0}
                  data={{}}
                />
              ))}
            </div>
          </div>

          <div className="flex items-center gap-3 w-full">
            <div className="w-1/6 text-sm font-medium text-[#586071]">
              Versements reçus
            </div>
            <div className="flex w-5/6 flex-wrap gap-2">
              {displayArrays.virDisplay.map((v, i) => (
                <FieldInput
                  key={`funder-${group.funder.id}-vir-recu-${year}-${i}`}
                  type={6}
                  defaultValue={v || 0}
                  valueMultiple={v || 0}
                  data={{}}
                />
              ))}
            </div>
          </div>
        </div>

        <div className="rounded-3xl bg-[#F7FFF5] px-4 py-4">
          {indicatorsLoading && (
            <div className="mb-3 inline-flex items-center gap-2 rounded-full bg-white px-3 py-1 text-[11px] font-medium text-slate-600 ring-1 ring-slate-200">
              <Loader2 className="h-3.5 w-3.5 animate-spin" />
              Chargement des indicateurs terrain...
            </div>
          )}
          <div className="flex items-center gap-3 mb-3 w-full">
            <div className="w-1/6 text-sm font-medium text-[#586071]">
              Versements terrain prévus
            </div>
            <div className="flex w-5/6 flex-wrap gap-2">
              {displayArrays.indPrevus.map((v, i) => (
                <FieldInput
                  key={`funder-${group.funder.id}-terrain-prev-${year}-${i}`}
                  type={1}
                  defaultValue={v || 0}
                  valueMultiple={v || 0}
                  data={{}}
                />
              ))}
            </div>
          </div>

          <div className="flex items-center gap-3 mb-3 w-full">
            <div className="w-1/6 text-sm font-medium text-[#586071]">
              Versements terrain réalisés
            </div>
            <div className="flex w-5/6 flex-wrap gap-2">
              {displayArrays.indRealises.map((v, i) => (
                <FieldInput
                  key={`funder-${group.funder.id}-terrain-real-${year}-${i}`}
                  type={2}
                  defaultValue={v || 0}
                  valueMultiple={v || 0}
                  data={{}}
                />
              ))}
            </div>
          </div>

          <div className="flex items-center gap-3 w-full">
            <div className="w-1/6 text-sm font-medium text-[#586071]">
              Versements terrain certifiés
            </div>
            <div className="flex w-5/6 flex-wrap gap-2">
              {displayArrays.indCertifies.map((v, i) => (
                <FieldInput
                  key={`funder-${group.funder.id}-terrain-cert-${year}-${i}`}
                  type={3}
                  defaultValue={v || 0}
                  valueMultiple={v || 0}
                  data={{}}
                />
              ))}
            </div>
          </div>
        </div>
      </div>

      <div
        className="mt-6 flex items-center gap-3"
        data-tour-funding-funder-project-detail="true"
      >
        <div className="inline-flex items-center gap-2 rounded-full bg-white px-3 py-1 text-xs font-semibold text-slate-700 shadow-sm ring-1 ring-slate-200">
          <span className="h-1.5 w-1.5 rounded-full bg-slate-400" />
          Détail par projet (contrats & versements)
        </div>
        <div className="h-px flex-1 bg-slate-200" />
      </div>

      <div className="mt-3 space-y-3">
        {group.projects.map((p) => (
          <ProjectDetailCard
            key={p.id}
            year={year}
            funderId={group.funder.id}
            project={p}
            funders={funders}
            setFunders={setFunders}
            onProjectFundingsUpdated={onProjectFundingsUpdated}
            onlyOneFunder={onlyOneFunder}
            forceEuroDisplay={forceEuroDisplay}
            cardDomId={`funding-funder-project-${group.funder.id}-${p.id}`}
          />
        ))}
      </div>
    </>
  );
}

/**
 * ✅ Version EDITABLE (pas readonly)
 * - si idFunder fourni => n'affiche QUE ce financeur
 * - cache la recherche
 * - enlève le fond (bg) du Card wrapper
 * - ✅ Ajout financement global par financeur (programme → projet)
 */
export default function NewFundingByFunder({
  year,
  idFunder,
  forceEuroDisplay = false,
}: {
  year: number;
  idFunder?: number;
  forceEuroDisplay?: boolean;
}) {
  const { fetchData } = useFetchDataV2("");

  const [funders, setFunders] = useState<Funder[]>([]);
  const [loadingFunders, setLoadingFunders] = useState(true);

  const [projectsToSale, setProjectsToSale] = useState<SaleProject[]>([]);
  const [loadingProjectsToSale, setLoadingProjectsToSale] = useState(false);
  const projectsToSalePromiseRef = useRef<Promise<SaleProject[]> | null>(null);

  const [funderProjectsById, setFunderProjectsById] = useState<
    Record<number, ProjectApi[]>
  >({});
  const [loadingFunderIds, setLoadingFunderIds] = useState<
    Record<number, boolean>
  >({});
  const [loadedFunderIds, setLoadedFunderIds] = useState<
    Record<number, boolean>
  >({});

  const [projectIndicatorsByProjectId, setProjectIndicatorsByProjectId] =
    useState<Record<number, ProjectIndicator[]>>({});
  const [globalIndicatorsByProjectId, setGlobalIndicatorsByProjectId] =
    useState<Record<number, ProjectIndicator[]>>({});
  const [loadingIndicatorsByProjectId, setLoadingIndicatorsByProjectId] =
    useState<Record<number, boolean>>({});
  const [trackingSummaryByFunderId, setTrackingSummaryByFunderId] = useState<
    Record<number, FunderTrackingSummary>
  >({});
  const [loadingTrackingFunding, setLoadingTrackingFunding] = useState(true);
  const [loadingTrackingIndicators, setLoadingTrackingIndicators] =
    useState(true);

  const [trackingSearch, setTrackingSearch] = useState("");
  const [trackingFilter, setTrackingFilter] =
    useState<FunderTrackingFilterKey>("all");
  const [openIds, setOpenIds] = useState<Record<number, boolean>>({});
  const [funderNameOverrides, setFunderNameOverrides] = useState<
    Record<number, string>
  >({});

  const onlyOneFunder = typeof idFunder === "number";

  // ✅ modal global
  const [openAddGlobal, setOpenAddGlobal] = useState(false);
  const [addGlobalFunder, setAddGlobalFunder] = useState<Funder | null>(null);
  const [blockedProjectIds, setBlockedProjectIds] = useState<number[]>([]);

  const fallbackFundingSummaryHydratedFunderIdsRef = useRef<
    Record<number, true>
  >({});
  const funderLoadPromisesRef = useRef<Partial<Record<number, Promise<void>>>>(
    {},
  );
  const indicatorLoadPromisesRef = useRef<
    Partial<Record<number, Promise<ProjectIndicator[]>>>
  >({});

  useEffect(() => {
    let canceled = false;
    const loadFunders = async () => {
      setLoadingFunders(true);
      try {
        const res = await fetchData(
          `${import.meta.env.VITE_API_ENDPOINT}/financers`,
        );
        if (canceled) return;
        const next = Array.isArray(res) ? res : [];
        setFunders(
          next.map((f: any) => ({
            ...f,
            id: Number(f?.id),
            fullname: f?.fullname || f?.structureName || `Financeur #${f?.id}`,
          })),
        );
      } finally {
        if (!canceled) setLoadingFunders(false);
      }
    };

    void loadFunders();
    return () => {
      canceled = true;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    let canceled = false;

    const loadTrackingSummary = async () => {
      setLoadingTrackingFunding(true);
      setLoadingTrackingIndicators(true);
      setTrackingSummaryByFunderId({});

      const loadFunding = async () => {
        try {
          const fundingRes = await fetchData(
            `${import.meta.env.VITE_API_ENDPOINT}/real-fundings?by=project`,
          );
          if (canceled) return;
          setTrackingSummaryByFunderId((prev) =>
            mergeFunderTrackingSummaryMaps(
              prev,
              buildFunderTrackingSummaryById(fundingRes, []),
            ),
          );
        } catch {
          if (canceled) return;
          // conserve les valeurs précédentes si l'API funding échoue
        } finally {
          if (!canceled) setLoadingTrackingFunding(false);
        }
      };

      const loadIndicators = async () => {
        try {
          const indicatorsRes = await fetchData(
            `${import.meta.env.VITE_API_ENDPOINT}/indicator/data/virement`,
          );
          if (canceled) return;
          setTrackingSummaryByFunderId((prev) =>
            mergeFunderTrackingSummaryMaps(
              prev,
              buildFunderTrackingSummaryById([], indicatorsRes),
            ),
          );
          const indicators = Array.isArray(indicatorsRes)
            ? indicatorsRes
            : Array.isArray((indicatorsRes as any)?.data)
              ? (indicatorsRes as any).data
              : [];
          const nextByProjectId: Record<number, ProjectIndicator[]> = {};
          for (const indicator of indicators) {
            const projectId = readProjectIdFromIndicator(indicator);
            if (!projectId) continue;
            nextByProjectId[projectId] = nextByProjectId[projectId] || [];
            nextByProjectId[projectId].push(indicator);
          }
          setGlobalIndicatorsByProjectId(nextByProjectId);
          setProjectIndicatorsByProjectId((prev) => ({
            ...prev,
            ...nextByProjectId,
          }));
        } catch {
          if (canceled) return;
          // conserve les valeurs précédentes si l'API indicateurs échoue
        } finally {
          if (!canceled) setLoadingTrackingIndicators(false);
        }
      };

      void loadFunding();
      void loadIndicators();
    };

    void loadTrackingSummary();
    return () => {
      canceled = true;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    let canceled = false;
    if (loadingTrackingFunding) return;

    const funderIds = (funders || [])
      .map((funder) => Number(funder?.id))
      .filter((id) => Number.isFinite(id) && id > 0);
    if (!funderIds.length) return;
    const missingFundingSummaryFunderIds = funderIds.filter((funderId) => {
      if (fallbackFundingSummaryHydratedFunderIdsRef.current[funderId]) {
        return false;
      }
      const summary = trackingSummaryByFunderId[Number(funderId)];
      return !hasFunderTrackingFundingSignals(summary);
    });
    if (!missingFundingSummaryFunderIds.length) return;
    for (const funderId of missingFundingSummaryFunderIds) {
      fallbackFundingSummaryHydratedFunderIdsRef.current[funderId] = true;
    }

    const hydrateFromFunderEndpoints = async () => {
      const partialSummary: Record<number, FunderTrackingSummary> = {};
      const partialProjectsByFunderId: Record<number, ProjectApi[]> = {};

      for (const funderId of missingFundingSummaryFunderIds) {
        if (canceled) break;
        try {
          const res = await fetchData(
            `${import.meta.env.VITE_API_ENDPOINT}/real-fundings?by=financer&id=${funderId}`,
          );
          const projects = normalizeProjectsForUI(
            extractProjectsFromFunderFundingResponse(res, funderId),
          );
          const summary = buildFunderTrackingSummaryFromProjects(
            projects,
            funderId,
          );
          if (projects.length > 0) {
            partialProjectsByFunderId[funderId] = projects;
          }
          if (hasFunderTrackingFundingSignals(summary)) {
            partialSummary[funderId] = summary;
          }
        } catch {
          // noop: on continue les autres financeurs
        }
      }

      if (canceled) return;
      if (Object.keys(partialProjectsByFunderId).length > 0) {
        setFunderProjectsById((prev) => {
          const next = { ...prev };
          for (const [funderIdRaw, projects] of Object.entries(
            partialProjectsByFunderId,
          )) {
            const funderId = Number(funderIdRaw);
            if (!funderId || Number.isNaN(funderId)) continue;
            if (Array.isArray(next[funderId]) && next[funderId].length > 0) {
              continue;
            }
            next[funderId] = projects;
          }
          return next;
        });
      }
      if (Object.keys(partialSummary).length > 0) {
        setTrackingSummaryByFunderId((prev) =>
          mergeFunderTrackingSummaryMaps(prev, partialSummary),
        );
      }
    };

    void hydrateFromFunderEndpoints();
    return () => {
      canceled = true;
    };
  }, [fetchData, funders, loadingTrackingFunding, trackingSummaryByFunderId]);

  const ensureProjectsToSaleLoaded = useCallback(async (): Promise<
    SaleProject[]
  > => {
    if (projectsToSale.length > 0) return projectsToSale;
    if (projectsToSalePromiseRef.current)
      return projectsToSalePromiseRef.current;

    setLoadingProjectsToSale(true);
    const promise = fetchData(
      `${import.meta.env.VITE_API_ENDPOINT}/projects-to-sale`,
    )
      .then((res) => {
        const next = Array.isArray(res) ? res : [];
        setProjectsToSale(next);
        return next as SaleProject[];
      })
      .finally(() => {
        setLoadingProjectsToSale(false);
        projectsToSalePromiseRef.current = null;
      });

    projectsToSalePromiseRef.current = promise;
    return promise;
  }, [fetchData, projectsToSale]);

  const ensureProjectIndicatorsLoaded = useCallback(
    async (projectId: number): Promise<ProjectIndicator[]> => {
      if (!projectId || Number.isNaN(projectId)) return [];
      if (projectIndicatorsByProjectId[projectId]) {
        return projectIndicatorsByProjectId[projectId];
      }
      if (indicatorLoadPromisesRef.current[projectId]) {
        return indicatorLoadPromisesRef.current[projectId]!;
      }

      setLoadingIndicatorsByProjectId((prev) => ({
        ...prev,
        [projectId]: true,
      }));
      const promise = Promise.resolve()
        .then(() => {
          const next = Array.isArray(globalIndicatorsByProjectId[projectId])
            ? globalIndicatorsByProjectId[projectId]
            : [];
          setProjectIndicatorsByProjectId((prev) => ({
            ...prev,
            [projectId]: next,
          }));
          return next as ProjectIndicator[];
        })
        .finally(() => {
          setLoadingIndicatorsByProjectId((prev) => ({
            ...prev,
            [projectId]: false,
          }));
          delete indicatorLoadPromisesRef.current[projectId];
        });

      indicatorLoadPromisesRef.current[projectId] = promise;
      return promise;
    },
    [globalIndicatorsByProjectId, projectIndicatorsByProjectId],
  );

  const ensureFunderDataLoaded = useCallback(
    async (funderId: number) => {
      if (!funderId || Number.isNaN(funderId)) return;
      if (loadedFunderIds[funderId]) return;
      if (funderLoadPromisesRef.current[funderId]) {
        return funderLoadPromisesRef.current[funderId];
      }

      const promise = (async () => {
        setLoadingFunderIds((prev) => ({ ...prev, [funderId]: true }));
        try {
          const res = await fetchData(
            `${import.meta.env.VITE_API_ENDPOINT}/real-fundings?by=financer&id=${funderId}`,
          );

          const projectsWithUi = normalizeProjectsForUI(
            extractProjectsFromFunderFundingResponse(res, funderId),
          );
          const projects =
            await normalizeProjectsContractAmountsForEurDisplay(projectsWithUi);
          setFunderProjectsById((prev) => ({ ...prev, [funderId]: projects }));

          const funderCandidates = (Array.isArray(res) ? res : []).concat(
            Array.isArray((res as any)?.data) ? (res as any).data : [],
          );
          const target =
            funderCandidates.find(
              (f: any) => Number(f?.id) === Number(funderId),
            ) ?? funderCandidates[0];
          const fallbackName = target?.fullname || target?.structureName;
          if (fallbackName) {
            setFunderNameOverrides((prev) => ({
              ...prev,
              [funderId]: String(fallbackName),
            }));
          }

          await Promise.all(
            projects.map((p) => ensureProjectIndicatorsLoaded(Number(p.id))),
          );

          setLoadedFunderIds((prev) => ({ ...prev, [funderId]: true }));
        } finally {
          setLoadingFunderIds((prev) => ({ ...prev, [funderId]: false }));
          delete funderLoadPromisesRef.current[funderId];
        }
      })();

      funderLoadPromisesRef.current[funderId] = promise;
      return promise;
    },
    [ensureProjectIndicatorsLoaded, fetchData, loadedFunderIds],
  );

  useEffect(() => {
    if (!onlyOneFunder || !idFunder) return;
    const fid = Number(idFunder);
    setOpenIds((prev) => {
      if (prev[fid]) return prev;
      return { ...prev, [fid]: true };
    });

    if (loadedFunderIds[fid] || loadingFunderIds[fid]) return;
    void ensureFunderDataLoaded(fid);
  }, [
    ensureFunderDataLoaded,
    idFunder,
    onlyOneFunder,
    loadedFunderIds,
    loadingFunderIds,
  ]);

  const onProjectFundingsUpdated = useCallback(
    (_funderId: number, projectId: number, apiResult: any) => {
      const nextFundingsRaw = extractRealFundings(apiResult);
      const deletedId = Number((apiResult as any)?.id);

      void (async () => {
        const nextFundings =
          nextFundingsRaw.length > 0
            ? await normalizeFundingsContractAmountsForEurDisplay(
                normalizeFundingsForUI(nextFundingsRaw),
              )
            : [];

        setFunderProjectsById((prev) => {
          const next: Record<number, ProjectApi[]> = { ...prev };

          for (const [fidRaw, projects] of Object.entries(prev)) {
            const fid = Number(fidRaw);
            const updatedProjects = (projects || []).map((p) => {
              if (Number(p.id) !== Number(projectId)) return p;

              if (nextFundings.length > 0) {
                return { ...p, realFundings: nextFundings };
              }

              if (deletedId && !Number.isNaN(deletedId)) {
                return {
                  ...p,
                  realFundings: (p.realFundings || []).filter(
                    (rf) => Number((rf as any)?.id) !== deletedId,
                  ),
                };
              }

              return p;
            });

            next[fid] = normalizeProjectsForUI(updatedProjects);
          }

          return next;
        });
      })();
    },
    [],
  );

  const groups: FunderGroup[] = useMemo(() => {
    const buildGroup = (
      funderId: number,
      funderName: string,
      sourceProjects: ProjectApi[],
    ): FunderGroup => {
      const projectsArr = (sourceProjects || [])
        .map((p) => ({
          projectRaw: p,
          id: Number(p.id),
          name: p.name || `Projet #${p.id}`,
          fundings: (Array.isArray(p?.realFundings)
            ? p.realFundings
            : []
          ).filter((rf) => {
            const fid = readFunderIdFromFunding(rf);
            return !fid || Number(fid) === Number(funderId);
          }),
        }))
        .filter((p) => !!p.id && !Number.isNaN(p.id))
        .sort((a, b) => a.name.localeCompare(b.name, "fr"));

      const allFundings = projectsArr.flatMap((x) => x.fundings);
      const { recDisplay, virDisplayPrevu, virDisplay } = computeFundingArrays(
        year,
        allFundings,
      );

      const { desiredDisplay, desiredCount, fallbackJanCount } =
        computeDesiredArrays(
          year,
          projectsArr.map((p) => p.projectRaw),
        );

      const prev = Array(12).fill(0) as number[];
      const real = Array(12).fill(0) as number[];
      const cert = Array(12).fill(0) as number[];

      const indicatorsForFunder = projectsArr.flatMap(
        (proj) => projectIndicatorsByProjectId[Number(proj.id)] || [],
      );

      for (const proj of projectsArr) {
        const t = computeTerrainArrays(
          year,
          indicatorsForFunder,
          proj.id,
          funderId,
        );
        for (let i = 0; i < 12; i++) {
          prev[i] += t.indPrevus[i] || 0;
          real[i] += t.indRealises[i] || 0;
          cert[i] += t.indCertifies[i] || 0;
        }
      }

      return {
        funder: {
          id: funderId,
          fullname: funderName || `Financeur #${funderId}`,
        },
        projects: projectsArr,
        desiredDisplay,
        desiredCount,
        desiredFallbackJanCount: fallbackJanCount,
        recDisplay,
        virDisplayPrevu,
        virDisplay,
        indPrevus: prev,
        indRealises: real,
        indCertifies: cert,
      };
    };

    const list: FunderGroup[] = [];
    const sourceFunders =
      typeof idFunder === "number"
        ? funders.filter((f) => Number(f.id) === Number(idFunder))
        : funders;

    for (const f of sourceFunders) {
      const fid = Number(f.id);
      const name =
        f.fullname || funderNameOverrides[fid] || `Financeur #${f.id}`;
      const projects = normalizeProjectsForUI(funderProjectsById[fid] || []);
      list.push(buildGroup(fid, name, projects));
    }

    if (
      typeof idFunder === "number" &&
      !list.some((g) => Number(g.funder.id) === Number(idFunder))
    ) {
      const fid = Number(idFunder);
      list.push(
        buildGroup(
          fid,
          funderNameOverrides[fid] || `Financeur #${fid}`,
          normalizeProjectsForUI(funderProjectsById[fid] || []),
        ),
      );
    }

    list.sort((a, b) =>
      a.funder.fullname.localeCompare(b.funder.fullname, "fr"),
    );
    return list;
  }, [
    funders,
    funderNameOverrides,
    funderProjectsById,
    projectIndicatorsByProjectId,
    year,
    idFunder,
  ]);

  const normalizedTrackingSearch = (trackingSearch || "").trim().toLowerCase();

  const getTrackingSummary = useCallback(
    (funderId: number): FunderTrackingSummary | null => {
      return trackingSummaryByFunderId[Number(funderId)] || null;
    },
    [trackingSummaryByFunderId],
  );

  const hasNeedsForGroup = useCallback(
    (group: FunderGroup) => {
      const summary = getTrackingSummary(group.funder.id);
      return !!summary?.needs;
    },
    [getTrackingSummary],
  );
  const hasContractsForGroup = useCallback(
    (group: FunderGroup) => {
      const summary = getTrackingSummary(group.funder.id);
      return !!summary?.contracts;
    },
    [getTrackingSummary],
  );
  const hasVerdeoContractsForGroup = useCallback(
    (group: FunderGroup) => {
      const summary = getTrackingSummary(group.funder.id);
      return !!summary?.contractsVerdeo;
    },
    [getTrackingSummary],
  );
  const hasInternalContractsForGroup = useCallback(
    (group: FunderGroup) => {
      const summary = getTrackingSummary(group.funder.id);
      return !!summary?.contractsInternal;
    },
    [getTrackingSummary],
  );
  const hasPlannedForGroup = useCallback(
    (group: FunderGroup) => {
      const summary = getTrackingSummary(group.funder.id);
      return !!summary?.planned;
    },
    [getTrackingSummary],
  );
  const hasReceivedForGroup = useCallback(
    (group: FunderGroup) => {
      const summary = getTrackingSummary(group.funder.id);
      return !!summary?.received;
    },
    [getTrackingSummary],
  );
  const hasTerrainForGroup = useCallback(
    (group: FunderGroup) => {
      const summary = getTrackingSummary(group.funder.id);
      return !!summary?.terrain;
    },
    [getTrackingSummary],
  );

  const trackingStats = useMemo<IntroStat[]>(
    () => [
      {
        key: "needs",
        label: "Besoins à financer",
        value: groups.filter((group) => hasNeedsForGroup(group)).length,
        suffix: "financeurs",
        tone: "sand",
      },
      {
        key: "contracts_verdeo",
        label: "Contrats via Verdeo",
        value: groups.filter((group) => hasVerdeoContractsForGroup(group))
          .length,
        suffix: "financeurs",
        tone: "olive",
      },
      {
        key: "contracts_internal",
        label: "Contrats internes",
        value: groups.filter((group) => hasInternalContractsForGroup(group))
          .length,
        suffix: "financeurs",
        tone: "blue",
      },
      {
        key: "all",
        label: "Tous",
        value: groups.length,
        suffix: "financeurs",
        tone: "slate",
      },
      {
        key: "planned",
        label: "Versements prévus",
        value: groups.filter((group) => hasPlannedForGroup(group)).length,
        suffix: "financeurs",
        tone: "teal",
      },
      {
        key: "received",
        label: "Versements reçus",
        value: groups.filter((group) => hasReceivedForGroup(group)).length,
        suffix: "financeurs",
        tone: "violet",
      },
      {
        key: "terrain",
        label: "Versements terrain",
        value: groups.filter((group) => hasTerrainForGroup(group)).length,
        suffix: "financeurs",
        tone: "rose",
      },
    ],
    [
      groups,
      hasContractsForGroup,
      hasInternalContractsForGroup,
      hasNeedsForGroup,
      hasPlannedForGroup,
      hasReceivedForGroup,
      hasTerrainForGroup,
      hasVerdeoContractsForGroup,
    ],
  );

  const introLoadingStatKeys = useMemo(() => {
    const keys = new Set<string>();
    if (loadingTrackingFunding) {
      keys.add("needs");
      keys.add("contracts_verdeo");
      keys.add("contracts_internal");
      keys.add("planned");
      keys.add("received");
    }
    if (loadingTrackingIndicators) {
      keys.add("terrain");
    }
    return Array.from(keys);
  }, [loadingTrackingFunding, loadingTrackingIndicators]);

  const filtered = useMemo(() => {
    return groups.filter((group) => {
      if (normalizedTrackingSearch) {
        const haystack = [String(group.funder.fullname ?? "")]
          .join(" ")
          .toLowerCase();
        if (!haystack.includes(normalizedTrackingSearch)) return false;
      }
      switch (trackingFilter) {
        case "needs":
          return hasNeedsForGroup(group);
        case "contracts_verdeo":
          return hasVerdeoContractsForGroup(group);
        case "contracts_internal":
          return hasInternalContractsForGroup(group);
        case "contracts":
          return hasContractsForGroup(group);
        case "planned":
          return hasPlannedForGroup(group);
        case "received":
          return hasReceivedForGroup(group);
        case "terrain":
          return hasTerrainForGroup(group);
        case "all":
        default:
          return true;
      }
    });
  }, [
    groups,
    hasContractsForGroup,
    hasInternalContractsForGroup,
    hasNeedsForGroup,
    hasPlannedForGroup,
    hasReceivedForGroup,
    hasTerrainForGroup,
    hasVerdeoContractsForGroup,
    normalizedTrackingSearch,
    trackingFilter,
  ]);

  const introCalendarEvents = useMemo(() => {
    const events: CalendarEventInput[] = [];
    for (const group of filtered) {
      const summary = trackingSummaryByFunderId[Number(group.funder.id)];
      if (summary?.calendarEvents?.length) {
        events.push(...summary.calendarEvents);
      }
    }
    return events;
  }, [filtered, trackingSummaryByFunderId]);

  const introReminderItems = useMemo(() => {
    const reminders: FundingReminderItem[] = [];
    for (const group of filtered) {
      const summary = trackingSummaryByFunderId[Number(group.funder.id)];
      if (summary?.reminderItems?.length) {
        reminders.push(...summary.reminderItems);
      }
      if (summary?.calendarEvents?.length) {
        reminders.push(
          ...collectReminderItemsFromCalendarEvents(summary.calendarEvents),
        );
      }
    }
    return Array.from(
      new Map(
        reminders.map((item) => [
          [
            item?.id || "",
            item?.section || "",
            String(item?.label || "").trim(),
            toDayKey(toDate(item?.date) || new Date(0)) || "",
            item?.targetId || "",
            Number(item?.projectId) || 0,
            Number(item?.funderId) || 0,
          ].join("|"),
          item,
        ]),
      ).values(),
    );
  }, [filtered, trackingSummaryByFunderId]);

  const handleOpenAddFundingGlobal = (payload: {
    funder: Funder;
    blockedProjectIds: number[];
  }) => {
    setAddGlobalFunder(payload.funder);
    setBlockedProjectIds(payload.blockedProjectIds || []);
    setOpenAddGlobal(true);
    void ensureProjectsToSaleLoaded();
  };

  const headerActionTarget = useMemo(() => {
    if (onlyOneFunder && typeof idFunder === "number") {
      return filtered.find(
        (group) => Number(group.funder.id) === Number(idFunder),
      );
    }
    return filtered[0] || null;
  }, [filtered, idFunder, onlyOneFunder]);

  const handleHeaderAction = useCallback(() => {
    if (!headerActionTarget) return;
    handleOpenAddFundingGlobal({
      funder: {
        id: Number(headerActionTarget.funder.id),
        fullname: headerActionTarget.funder.fullname,
      },
      blockedProjectIds: headerActionTarget.projects.map((project) =>
        Number(project.id),
      ),
    });
  }, [handleOpenAddFundingGlobal, headerActionTarget]);

  const handleResetIntro = useCallback(() => {
    setTrackingSearch("");
    setTrackingFilter("all");
  }, []);

  const handleCalendarEventClick = useCallback(
    (item: CalendarDetailItem) => {
      const funderId = Number(item?.funderId);
      if (funderId && !Number.isNaN(funderId)) {
        setOpenIds((prev) => ({ ...prev, [funderId]: true }));
        void ensureFunderDataLoaded(funderId);
      }

      const targetId =
        item?.targetId ||
        (funderId && !Number.isNaN(funderId)
          ? `funding-funder-card-${funderId}`
          : undefined);
      if (!targetId) return;

      const scrollToTarget = () => {
        const node = document.getElementById(targetId);
        if (!node) return;
        node.scrollIntoView({ behavior: "smooth", block: "start" });
      };
      window.requestAnimationFrame(scrollToTarget);
      window.setTimeout(scrollToTarget, 220);
      window.setTimeout(scrollToTarget, 700);
    },
    [ensureFunderDataLoaded],
  );

  const handleReminderClick = useCallback(
    (item: FundingReminderItem) => {
      handleCalendarEventClick({
        label: item.label,
        count: 1,
        targetId: item.targetId,
        projectId: item.projectId,
        funderId: item.funderId,
      });
    },
    [handleCalendarEventClick],
  );

  const handleToggleFunder = (funderId: number, isOpen: boolean) => {
    const nextOpen = !isOpen;
    setOpenIds((m) => ({ ...m, [funderId]: nextOpen }));
    if (nextOpen) void ensureFunderDataLoaded(funderId);
  };

  if (loadingFunders) return <FundingSkeleton />;

  return (
    <div className="space-y-6" data-tour-funding-funder-root>
      <FundingTrackingTutorial
        mode="funder"
        showFunderSearch={!onlyOneFunder}
      />

      <AddFundingGlobalModal
        open={openAddGlobal}
        onOpenChange={setOpenAddGlobal}
        year={year}
        funder={addGlobalFunder}
        projectsToSale={projectsToSale}
        loadingProjectsToSale={loadingProjectsToSale}
        blockedProjectIds={blockedProjectIds}
      />

      <div data-tour-funding-funder-search="true">
        <FundingTrackingIntroPanel
          title="Suivi de contribution"
          badge={`${filtered.length} financeur(s)`}
          subtitle="Pilotez les contrats et les versements par financeur, puis descendez au détail par projet."
          searchPlaceholder="Rechercher un financeur…"
          searchValue={trackingSearch}
          onSearchChange={setTrackingSearch}
          onReset={handleResetIntro}
          resultsCount={filtered.length}
          stats={trackingStats}
          activeStatKey={trackingFilter}
          onSelectStat={(key) =>
            setTrackingFilter(key as FunderTrackingFilterKey)
          }
          actionLabel={
            headerActionTarget ? "+ J’ai trouvé un financement" : undefined
          }
          onActionClick={headerActionTarget ? handleHeaderAction : undefined}
          calendarEvents={introCalendarEvents}
          onCalendarEventClick={handleCalendarEventClick}
          reminderItems={introReminderItems}
          onReminderClick={handleReminderClick}
          loadingStatKeys={introLoadingStatKeys}
        />
      </div>

      {filtered.length === 0 ? (
        <div className="rounded-xl border bg-white p-4 text-sm text-muted-foreground">
          Aucun financeur trouvé.
        </div>
      ) : (
        filtered.map((g, index) => {
          const isOpen = !!openIds[g.funder.id];
          const isFunderLoading = !!loadingFunderIds[g.funder.id];
          const isFunderLoaded = !!loadedFunderIds[g.funder.id];
          const indicatorsLoading =
            isFunderLoading ||
            g.projects.some(
              (p) => !!loadingIndicatorsByProjectId[Number(p.id)],
            );

          if (onlyOneFunder) {
            if (!isOpen) return null;

            return (
              <Card
                key={g.funder.id}
                id={`funding-funder-card-${g.funder.id}`}
                className="border-none bg-transparent"
                data-tour-funding-funder-card="true"
              >
                <CardContent className="p-0">
                  {!isFunderLoaded ? (
                    <FundingSkeleton />
                  ) : (
                    <GroupContent
                      year={year}
                      group={g}
                      funders={funders}
                      setFunders={setFunders}
                      onProjectFundingsUpdated={onProjectFundingsUpdated}
                      indicatorsLoading={indicatorsLoading}
                      onlyOneFunder={onlyOneFunder}
                      onOpenAddFundingGlobal={handleOpenAddFundingGlobal}
                      forceEuroDisplay={forceEuroDisplay}
                    />
                  )}
                </CardContent>
              </Card>
            );
          }

          return (
            <Card
              key={g.funder.id}
              id={`funding-funder-card-${g.funder.id}`}
              className="border-none bg-[#F7F9FB]"
              data-tour-funding-funder-card={index === 0 ? "true" : undefined}
            >
              <CardHeader className="flex flex-row items-center justify-between">
                <div className="min-w-0">
                  <div className="text-sm font-semibold text-[#434653] truncate">
                    {g.funder.fullname}
                  </div>
                  <div className="mt-1 text-[11px] text-muted-foreground">
                    {isFunderLoading
                      ? "Chargement..."
                      : isFunderLoaded
                        ? `${g.projects.length} projet(s)`
                        : "Déplier pour charger"}
                  </div>
                </div>

                <button
                  className="p-1"
                  onClick={() => handleToggleFunder(g.funder.id, isOpen)}
                  aria-label={isOpen ? "Réduire" : "Développer"}
                >
                  {isOpen ? (
                    <ChevronDown className="w-5 h-5" />
                  ) : (
                    <ChevronRight className="w-5 h-5" />
                  )}
                </button>
              </CardHeader>

              {isOpen && (
                <CardContent>
                  {!isFunderLoaded ? (
                    <FundingSkeleton />
                  ) : (
                    <GroupContent
                      year={year}
                      group={g}
                      funders={funders}
                      setFunders={setFunders}
                      onProjectFundingsUpdated={onProjectFundingsUpdated}
                      indicatorsLoading={indicatorsLoading}
                      onlyOneFunder={onlyOneFunder}
                      onOpenAddFundingGlobal={handleOpenAddFundingGlobal}
                      forceEuroDisplay={forceEuroDisplay}
                    />
                  )}
                </CardContent>
              )}
            </Card>
          );
        })
      )}
    </div>
  );
}
